import type {
  DismissalRuleCreateRequest,
  DismissalRuleListQuery,
  DismissalRuleUpdateRequest,
  FirstRunState,
  OrgAuditLogExportFormat,
  OrgAuditLogFilters,
  OrgAuditLogPage,
  OrgSettingsUpdate,
  PluginTokenSummary,
  ProjectNotificationListResponse,
  ProjectNotificationUnreadCountResponse,
  RevisionPublishExclusions,
  SavedItemCreateRequest,
  SavedItemListQuery,
  SavedItemUpdateRequest,
  RankHistoryResponse,
  RankKeywordMeasurement,
  RankRatingHistoryResponse,
  AppTimelineResponse,
  RankShareOfVoiceResponse,
  RankStoreId,
  WebsiteSourceStatus,
  OrgOverviewApp,
  StoreId,
  GenerateEventLocalizationsRequest,
  EventLocalizationStateRequest,
  SaveAppEventDraftRequest,
  UploadAppEventMediaRequest,
  SubmitAppEventForReviewRequest,
  SubmitFeaturingNominationRequest,
  WithdrawAppEventReviewRequest,
  DeleteAppEventDraftRequest,
  ExtensionPairStartRequest,
  ExtensionPairStartResponse,
  ExtensionTokenListResponse,
  KeywordAppKeywordsResponse,
  KeywordMarketSearchResponse,
  KeywordTermPageResponse,
  ListingPolicyCheckRequest,
  GenerateReviewReplyResponse,
  ReplyToReviewRequest,
  ReplyToReviewResponse,
  UserReviewsListParams,
  UserReviewsListResponse,
  UserReviewsRefreshResponse,
} from "@a7y/types";
import {
  apiOrigin,
  deploymentEnvironmentFromWebappHost,
  type HostedDeploymentEnvironment,
} from "@a7y/types/deployment-environment";

export type ApiClientConfig = {
  productId?: string;
  apiProdEndpoint?: string;
  forceProd?: boolean;
  baseUrl?: string | null;
  devPort?: number;
  fetchFn?: typeof fetch;
  shouldLog?: () => boolean;
  enableIdb?: boolean;
  /**
   * A7Y-1432: invoked once per request when the API responds with a 401 that
   * carries an authentication-class `reason` (e.g. `phantom_user`). The app
   * uses this to clear local session state and redirect to sign-in instead
   * of retrying with a fresh token (which is useless when the user's
   * Firestore doc has been cascade-deleted).
   *
   * Reason codes the API sends:
   *   - `phantom_user`  — Clerk JWT valid, but Users doc is gone. Hard sign-out.
   *
   * `token_expired` is NOT routed here; that one is handled per-call site by
   * clearing the in-memory token cache and retrying. See ActivityTile /
   * ActivityDetail for examples.
   */
  onAuthError?: (info: { reason: string; statusCode: number }) => void;
};

const DEFAULT_PRODUCT_ID = "apptonomy";
const DEFAULT_API_PROD_ENDPOINT = "https://api.apptonomy.ai";
const DEFAULT_DEV_PORT = 8922;

// Detect production mode across different environments
function detectIsProd(): boolean {
  // Check Vite's import.meta.env.PROD (set at build time)
  try {
    const meta = import.meta as { env?: { PROD?: boolean } };
    if (meta.env?.PROD === true) {
      return true;
    }
  } catch {
    // import.meta not available
  }

  // Check Node.js process.env.NODE_ENV
  if (typeof process !== "undefined" && process.env?.NODE_ENV === "production") {
    return true;
  }

  // Browser fallback: check if hostname is not localhost/dev
  if (typeof window !== "undefined" && window.location?.hostname) {
    const hostname = window.location.hostname;
    const isLocalDev =
      hostname === "localhost" ||
      hostname === "127.0.0.1" ||
      hostname.endsWith(".local") ||
      hostname.startsWith("192.168.") ||
      hostname.startsWith("10.");
    return !isLocalDev;
  }

  return false;
}

const IS_PROD = detectIsProd();

/**
 * A7Y SBX-03: classify the current browser hostname against the shared
 * deployment-environment contract (`@a7y/types/deployment-environment`)
 * rather than a bare `-staging.` substring test. Returns `undefined` for any
 * hostname that isn't one of the three deployed webapp hosts (local dev,
 * `*.workers.dev` previews, anything unrecognized) — those inputs fall
 * through to the pre-existing production-endpoint behavior below, exactly as
 * the old substring test did for everything that wasn't literally staging.
 */
function detectHostedEnvironment(): HostedDeploymentEnvironment | undefined {
  if (typeof window !== "undefined" && window.location?.hostname) {
    return deploymentEnvironmentFromWebappHost(window.location.hostname);
  }
  return undefined;
}

const HOSTED_ENVIRONMENT = detectHostedEnvironment();

export const StringType = {
  screentext: "screentext",
  storetext: "storetext",
  apptext: "apptext",
} as const;

let PRODUCT_ID = DEFAULT_PRODUCT_ID;
let FORCE_PROD = false;
let API_PROD_ENDPOINT = DEFAULT_API_PROD_ENDPOINT;
let BASE_URL_OVERRIDE: string | null = null;
let DEV_PORT = DEFAULT_DEV_PORT;
let FETCH_FN: typeof fetch | undefined = typeof fetch !== "undefined" ? fetch : undefined;
let SHOULD_LOG: (() => boolean) | undefined;
let ENABLE_IDB = true;
let ON_AUTH_ERROR: ((info: { reason: string; statusCode: number }) => void) | undefined;

// A7Y-1432: reason codes that mean "the user identity is permanently invalid
// for this session — clear local state and force a fresh sign-in". Distinct
// from `token_expired` (recoverable by minting a new JWT).
const HARD_AUTH_ERROR_REASONS: ReadonlySet<string> = new Set(["phantom_user"]);

function maybeFireAuthError(statusCode: number, errorBody: unknown): void {
  if (statusCode !== 401 || !ON_AUTH_ERROR) return;
  const reason =
    (errorBody as { reason?: string; message?: string } | null | undefined)?.reason ??
    (errorBody as { message?: string } | null | undefined)?.message;
  if (typeof reason === "string" && HARD_AUTH_ERROR_REASONS.has(reason)) {
    try {
      ON_AUTH_ERROR({ reason, statusCode });
    } catch (err) {
      // Never let the handler take the request path down.
      try {
        console.error("onAuthError handler threw", err);
      } catch {
        // ignore
      }
    }
  }
}

export type ApiSuccess<T = any> = {
  success: true;
  statusCode: number;
  data: T;
};

export type ApiFailure = {
  success: false;
  statusCode: number;
  statusText: string;
  error: any;
};

export type ApiResult<T = any> = ApiSuccess<T> | ApiFailure;

// ======================================================================================|
//  MARK: Auth-not-ready sentinel (A7Y-1311)
//
//  When a request requires auth but the token isn't ready yet (Clerk's
//  `getToken()` resolved to null/undefined during a brief signed-in-but-no-token
//  window), `makeApiRequest` short-circuits without hitting the network and
//  returns this sentinel-coded `ApiFailure`. Existing callers that just check
//  `result.success` get a clean `false` and treat it as a no-op. Callers that
//  want to distinguish "not ready" from a true failure can use `isApiNotReady`.

export const API_NOT_READY_REASON = "auth_token_not_ready" as const;

export type ApiNotReady = {
  success: false;
  statusCode: 0;
  statusText: "Auth token not ready";
  error: { message: typeof API_NOT_READY_REASON; reason: typeof API_NOT_READY_REASON };
};

export function isApiNotReady(result: unknown): result is ApiNotReady {
  return (
    !!result
    && typeof result === "object"
    && (result as { success?: unknown }).success === false
    && (result as { error?: { reason?: unknown } }).error?.reason === API_NOT_READY_REASON
  );
}

function makeNotReadyResult(): ApiNotReady {
  return {
    success: false,
    statusCode: 0,
    statusText: "Auth token not ready",
    error: { message: API_NOT_READY_REASON, reason: API_NOT_READY_REASON },
  };
}

export type ApiClient = {
  get<T = unknown>(url: string): Promise<T>;
  post<T = unknown>(url: string, body?: unknown): Promise<T>;
  patch<T = unknown>(url: string, body?: unknown): Promise<T>;
  /** A7Y-5210: the admin replaces HELM's slots, parked list and week plans with PUT. */
  put<T = unknown>(url: string, body?: unknown): Promise<T>;
  delete<T = unknown>(url: string): Promise<T>;
};

export type HttpClientConfig = ApiClientConfig & {
  credentials?: RequestCredentials;
  defaultHeaders?: Record<string, string>;
};

export function createApiClient(config: HttpClientConfig = {}): ApiClient {
  const resolvedBaseUrl = config.baseUrl ?? BASE_URL_OVERRIDE;
  const credentials = config.credentials;
  const defaultHeaders = config.defaultHeaders ?? {};
  const fetchImpl =
    config.fetchFn ?? FETCH_FN ?? (typeof fetch !== "undefined" ? fetch : undefined);

  if (!fetchImpl) {
    throw new Error("fetch is not available in this environment");
  }

  const buildUrl = (path: string) => {
    if (!resolvedBaseUrl) return path;
    const base = resolvedBaseUrl.endsWith("/")
      ? resolvedBaseUrl.slice(0, -1)
      : resolvedBaseUrl;
    const target = path.startsWith("/") ? path.slice(1) : path;
    return `${base}/${target}`;
  };

  const request = async <T>(
    method: "GET" | "POST" | "PATCH" | "PUT" | "DELETE",
    url: string,
    body?: unknown
  ) => {
    const headers: Record<string, string> = { ...defaultHeaders };
    const init: RequestInit = { method, credentials, headers };

    if (body !== undefined) {
      init.body = JSON.stringify(body);
      if (!headers["Content-Type"]) {
        headers["Content-Type"] = "application/json";
      }
    }

    const response = await fetchImpl(buildUrl(url), init);
    const contentType = response.headers.get("content-type") || "";
    const parse = async () => {
      if (contentType.includes("application/json")) {
        if (response.status === 204) return undefined as T;
        return (await response.json()) as T;
      }
      return (await response.text()) as unknown as T;
    };

    if (!response.ok) {
      const errorBody = await parse().catch(() => undefined);
      const detail = errorBody ? ` - ${JSON.stringify(errorBody)}` : "";
      throw new Error(
        `Request to ${url} failed: ${response.status} ${response.statusText}${detail}`
      );
    }

    return await parse();
  };

  return {
    get: (url) => request("GET", url),
    post: (url, body) => request("POST", url, body),
    patch: (url, body) => request("PATCH", url, body),
    put: (url, body) => request("PUT", url, body),
    delete: (url) => request("DELETE", url),
  };
}

type CachePolicy = {
  strategy?: "swr" | "network-only" | "bypass";
  memoryTtlMs?: number;
  persistMs?: number;
};

export function configureApiClient(config: ApiClientConfig = {}) {
  if (config.productId) PRODUCT_ID = config.productId;
  if (config.forceProd !== undefined) FORCE_PROD = config.forceProd;
  if (config.apiProdEndpoint) API_PROD_ENDPOINT = config.apiProdEndpoint;
  if (config.baseUrl !== undefined) BASE_URL_OVERRIDE = config.baseUrl ?? null;
  if (config.devPort !== undefined) DEV_PORT = config.devPort;
  if (config.fetchFn) FETCH_FN = config.fetchFn;
  if (config.shouldLog) SHOULD_LOG = config.shouldLog;
  if (config.enableIdb !== undefined) ENABLE_IDB = config.enableIdb;
  if (config.onAuthError) ON_AUTH_ERROR = config.onAuthError;
}

function doLog() {
  try {
    return SHOULD_LOG ? SHOULD_LOG() : false;
  } catch {
    return false;
  }
}

const API_ROOT_PATHS = {
  project: "project",
  user: "user",
  org: "org",
  webhook: "webhook",
  support: "support",
  contact: "contact",
  payment: "payment",
  messaging: "messaging",
  affiliate: "affiliate",
  // A7Y-3046 — the Coming Soon waiting list.
  featureInterest: "feature-interest",
  // A7Y-4165 — Chrome extension pairing and token management.
  extension: "extension",
};
const API_SUB_PATHS = {
  project_strings: `${API_ROOT_PATHS.project}/strings`,
  project_translations: `${API_ROOT_PATHS.project}/translations`,
  project_exports: `${API_ROOT_PATHS.project}/exports`,
  project_activity: `${API_ROOT_PATHS.project}/activity`,
  project_presence: `${API_ROOT_PATHS.project}/presence`,
  project_screens: `${API_ROOT_PATHS.project}/screens`,
  project_store_link: `${API_ROOT_PATHS.project}/store-link`,
  project_billing: `${API_ROOT_PATHS.project}/billing`,
  project_associated_apps: `${API_ROOT_PATHS.project}/associated-apps`,
  project_competitors: `${API_ROOT_PATHS.project}/competitors`,
  project_app_events: `${API_ROOT_PATHS.project}/app-events`,
  project_analytics: `${API_ROOT_PATHS.project}/analytics`,
  project_keywords: `${API_ROOT_PATHS.project}/keywords`,
  project_draft_listings: `${API_ROOT_PATHS.project}/draft-listings`,
  project_revisions: `${API_ROOT_PATHS.project}/revisions`,
  project_audit_schedule: `${API_ROOT_PATHS.project}/audit-schedule`,
  project_discovery_prompts: `${API_ROOT_PATHS.project}/discovery-prompts`,
  project_listing_history: `${API_ROOT_PATHS.project}/listing-history`,
  project_reviews: `${API_ROOT_PATHS.project}/reviews`,
  org_orgs: `${API_ROOT_PATHS.org}/orgs`,
};

// ======================================================================================|
//  MARK: Client-side Caching (Memory + IndexedDB)

// Simple in-memory cache and in-flight de-duplication
const __apiMemoryCache = new Map(); // key -> { data, fetchedAt, ttlMs, persistMs, etag, lastModified }
const __apiInFlight = new Map(); // key -> Promise

// IndexedDB minimal wrapper (no external deps)
const IDB_DB_NAME = "a7y-cache";
const IDB_STORE = "responses";
let __idbPromise = null;

function openIdb() {
  if (!ENABLE_IDB || typeof indexedDB === "undefined") return null;
  if (!__idbPromise) {
    __idbPromise = new Promise((resolve, reject) => {
      try {
        const req = indexedDB.open(IDB_DB_NAME, 1);
        req.onupgradeneeded = () => {
          const db = req.result;
          if (!db.objectStoreNames.contains(IDB_STORE)) {
            db.createObjectStore(IDB_STORE);
          }
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
      } catch {
        resolve(null);
      }
    });
  }
  return __idbPromise;
}

async function idbGet(key) {
  const db = await openIdb();
  if (!db) return null;
  return new Promise((resolve) => {
    const tx = db.transaction(IDB_STORE, "readonly");
    const store = tx.objectStore(IDB_STORE);
    const req = store.get(key);
    req.onsuccess = () => resolve(req.result || null);
    req.onerror = () => resolve(null);
  });
}

async function idbSet(key, value) {
  const db = await openIdb();
  if (!db) return false;
  return new Promise((resolve) => {
    const tx = db.transaction(IDB_STORE, "readwrite");
    const store = tx.objectStore(IDB_STORE);
    const req = store.put(value, key);
    req.onsuccess = () => resolve(true);
    req.onerror = () => resolve(false);
  });
}

async function idbClearAll() {
  const db = await openIdb();
  if (!db) return false;
  return new Promise((resolve) => {
    const tx = db.transaction(IDB_STORE, "readwrite");
    const store = tx.objectStore(IDB_STORE);
    const req = store.clear();
    req.onsuccess = () => resolve(true);
    req.onerror = () => resolve(false);
  });
}

async function idbDeleteWhere(predicate: (key: string) => boolean) {
  const db = await openIdb();
  if (!db) return 0;
  return new Promise((resolve) => {
    let count = 0;
    const tx = db.transaction(IDB_STORE, "readwrite");
    const store = tx.objectStore(IDB_STORE);
    const req = store.openCursor();
    req.onsuccess = () => {
      const cursor = req.result;
      if (cursor) {
        const key = cursor.key;
        if (typeof key === "string" && predicate(key)) {
          store.delete(key);
          count += 1;
        }
        cursor.continue();
      } else {
        resolve(count);
      }
    };
    req.onerror = () => resolve(count);
  });
}

async function idbDeleteByKeySubstring(substring) {
  return idbDeleteWhere((key) => key.includes(substring));
}

function nowMs() {
  return Date.now();
}

function decodeJwtUserScope(token) {
  if (!token) return "public";
  try {
    const parts = token.split(".");
    if (parts.length < 2) return `token:${token.slice(0, 8)}`;
    const payloadStr = atob(parts[1].replace(/-/g, "+").replace(/_/g, "/"));
    const payload = JSON.parse(payloadStr || "{}");
    const userId = payload?.sub || payload?.user_id || payload?.uid;
    const orgId = payload?.org_id || payload?.orgId;
    if (userId) return orgId ? `u:${userId}|o:${orgId}` : `u:${userId}`;
    return `token:${token.slice(0, 8)}`;
  } catch {
    return `token:${token?.slice(0, 8)}`;
  }
}

function buildCacheKey(scope, url) {
  return `${scope}|${url}`;
}

// A7Y-5842: the mutations that rewrite a project's list entry — PATCH/DELETE
// of the project itself, and the two sub-resources stored on the project doc.
function changesProjectListEntry(endpoint, method) {
  const parts = endpoint.split("/");
  if (parts[0] !== API_ROOT_PATHS.project || parts.length < 2) return false;
  if (parts.length === 2) return method === "PATCH" || method === "DELETE";
  return parts.length === 3 && (parts[2] === "add-app-entry" || parts[2] === "target-locales");
}

// A7Y-5842: the `GET /project` list entry, in any user scope. Matched on the
// key's suffix because `project/<id>` keys share the list URL as a prefix.
function isProjectListCacheKey(key) {
  return key.endsWith(`|${buildApiUrl(API_ROOT_PATHS.project, null)}`);
}

function getCachePolicy(endpoint, method): Required<CachePolicy> {
  if (method !== "GET") return { strategy: "network-only", memoryTtlMs: 0, persistMs: 0 };
  // Defaults
  let policy: Required<CachePolicy> = {
    strategy: "swr",
    memoryTtlMs: 2 * 60 * 1000,
    persistMs: 30 * 60 * 1000,
  };
  if (endpoint.startsWith(API_SUB_PATHS.project_strings)) {
    policy = { strategy: "swr", memoryTtlMs: 2 * 60 * 1000, persistMs: 60 * 60 * 1000 };
  } else if (endpoint.startsWith(API_SUB_PATHS.project_translations)) {
    policy = { strategy: "swr", memoryTtlMs: 60 * 1000, persistMs: 30 * 60 * 1000 };
  } else if (endpoint.startsWith(API_SUB_PATHS.project_screens)) {
    policy = { strategy: "swr", memoryTtlMs: 60 * 1000, persistMs: 15 * 60 * 1000 };
  } else if (endpoint.startsWith(API_SUB_PATHS.project_presence)) {
    policy = { strategy: "swr", memoryTtlMs: 2 * 60 * 1000, persistMs: 15 * 60 * 1000 };
  } else if (endpoint.startsWith(API_SUB_PATHS.project_draft_listings)) {
    policy = { strategy: "network-only", memoryTtlMs: 0, persistMs: 0 };
  } else if (endpoint.startsWith(API_SUB_PATHS.project_activity)) {
    policy = { strategy: "network-only", memoryTtlMs: 0, persistMs: 0 };
  } else if (endpoint.includes("/saved_items") || endpoint.includes("/dismissal_rules")) {
    policy = { strategy: "network-only", memoryTtlMs: 0, persistMs: 0 };
  } else if (endpoint === API_ROOT_PATHS.project) {
    policy = { strategy: "swr", memoryTtlMs: 5 * 60 * 1000, persistMs: 60 * 60 * 1000 };
  } else if (endpoint.startsWith(API_ROOT_PATHS.project + "/")) {
    policy = { strategy: "swr", memoryTtlMs: 2 * 60 * 1000, persistMs: 60 * 60 * 1000 };
  } else if (
    endpoint.startsWith(API_ROOT_PATHS.support) ||
    endpoint.startsWith(API_ROOT_PATHS.payment)
  ) {
    policy = { strategy: "network-only", memoryTtlMs: 0, persistMs: 0 };
  }
  return policy;
}

function isFresh(entry) {
  if (!entry) return false;
  return nowMs() - entry.fetchedAt < (entry.ttlMs || 0);
}

function isWithinPersist(entry) {
  if (!entry) return false;
  return nowMs() - entry.fetchedAt < (entry.persistMs || 0);
}

function rememberInMemory(key, data, ttlMs, persistMs, etag, lastModified) {
  __apiMemoryCache.set(key, {
    data,
    fetchedAt: nowMs(),
    ttlMs,
    persistMs,
    etag,
    lastModified,
  });
}

async function persistInIdb(key, data, ttlMs, persistMs, etag, lastModified) {
  await idbSet(key, {
    data,
    fetchedAt: nowMs(),
    ttlMs,
    persistMs,
    etag,
    lastModified,
  });
}

async function readFromCaches(key) {
  const mem = __apiMemoryCache.get(key);
  if (mem) return { source: "memory", entry: mem };
  const idb = await idbGet(key);
  if (idb) return { source: "idb", entry: idb };
  return { source: null, entry: null };
}

async function invalidateCachesForMutation(scope, endpoint, method, body) {
  // Coarse invalidation by endpoint family. Only clear keys within the same user scope.
  const scopePrefix = `${scope}|`;
  const predicates = [];
  if (endpoint.startsWith(API_SUB_PATHS.project_strings)) {
    // Clear strings and translations for the project
    const projectId = endpoint.split("/")[2] || body?.projectId;
    if (projectId) {
      predicates.push(
        `${scopePrefix}${buildApiUrl(
          `${API_SUB_PATHS.project_strings}/${projectId}`,
          null
        )}`
      );
      predicates.push(
        `${scopePrefix}${buildApiUrl(
          `${API_SUB_PATHS.project_translations}/${projectId}`,
          null
        )}`
      );
    }
  } else if (endpoint.startsWith(API_SUB_PATHS.project_translations)) {
    const projectId = body?.projectId || endpoint.split("/")[3];
    if (projectId)
      predicates.push(
        `${scopePrefix}${buildApiUrl(
          `${API_SUB_PATHS.project_translations}/${projectId}`,
          null
        )}`
      );
  } else if (endpoint.startsWith(API_SUB_PATHS.project_screens)) {
    const projectId = endpoint.split("/")[3] || body?.projectId;
    if (projectId)
      predicates.push(
        `${scopePrefix}${buildApiUrl(
          `${API_SUB_PATHS.project_screens}/${projectId}`,
          null
        )}`
      );
  } else if (endpoint.startsWith(API_ROOT_PATHS.project + "/")) {
    const projectId = endpoint.split("/")[1];
    predicates.push(
      `${scopePrefix}${buildApiUrl(`${API_ROOT_PATHS.project}/${projectId}`, null)}`
    );
    // A7Y-5842: the list now survives project switches (`keepProjectList`),
    // so a write to a project's own fields must drop the list's copy too.
    // Only those writes: `project/live-listing` and the other POST lookups
    // under `project/` change nothing in the list.
    if (changesProjectListEntry(endpoint, method)) {
      const listKey = buildCacheKey(scope, buildApiUrl(API_ROOT_PATHS.project, null));
      __apiMemoryCache.delete(listKey);
      await idbDeleteWhere((key) => key === listKey);
    }
  } else if (endpoint === API_ROOT_PATHS.project) {
    predicates.push(`${scopePrefix}${buildApiUrl(API_ROOT_PATHS.project, null)}`);
  } else if (endpoint.startsWith(API_SUB_PATHS.project_presence)) {
    const projectId = endpoint.split("/")[2];
    predicates.push(
      `${scopePrefix}${buildApiUrl(
        `${API_SUB_PATHS.project_presence}/${projectId}`,
        null
      )}`
    );
  } else if (endpoint.startsWith(API_SUB_PATHS.org_orgs)) {
    // Invalidate org members/invitations caches on any org mutation
    // Endpoint pattern: org/orgs/:orgId/members/:userId or org/orgs/:orgId/invitations
    const parts = endpoint.split("/");
    const orgId = parts[2];
    if (orgId) {
      predicates.push(
        `${scopePrefix}${buildApiUrl(
          `${API_SUB_PATHS.org_orgs}/${orgId}/members`,
          null
        )}`
      );
      predicates.push(
        `${scopePrefix}${buildApiUrl(
          `${API_SUB_PATHS.org_orgs}/${orgId}/invitations`,
          null
        )}`
      );
      // Also invalidate the org list itself
      predicates.push(
        `${scopePrefix}${buildApiUrl(API_SUB_PATHS.org_orgs, null)}`
      );
    }
  }

  // Delete from memory
  for (const key of __apiMemoryCache.keys()) {
    if (!key.startsWith(scopePrefix)) continue;
    if (predicates.some((sub) => key.includes(sub))) {
      __apiMemoryCache.delete(key);
    }
  }

  // Delete from IDB (best-effort)
  await Promise.all(predicates.map((sub) => idbDeleteByKeySubstring(sub)));
}

async function performNetworkFetch(url, method, token, body, conditional) {
  const headers: Record<string, string> = {};
  const request: RequestInit = {
    method,
    headers,
  };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  if (body) {
    request.body = JSON.stringify(body);
    headers["Content-Type"] = "application/json";
  }
  if (conditional?.etag) {
    headers["If-None-Match"] = conditional.etag;
  }
  if (conditional?.lastModified) {
    headers["If-Modified-Since"] = conditional.lastModified;
  }

  const fetchImpl = FETCH_FN ?? (typeof fetch !== "undefined" ? fetch : undefined);
  if (!fetchImpl) throw new Error("fetch is not available in this environment");
  const res = await fetchImpl(url, request);
  return res;
}

// ======================================================================================|
//  MARK: Cache Control API

/**
 * Clears the memory and IndexedDB response caches. `keepProjectList` keeps
 * the user-scoped `GET /project` list (A7Y-5842): a project switch needs fresh
 * per-project data, but wiping the list made every later reload refetch it
 * (measured 23 Oct 2026; source: A7Y-5842: 3.0–5.9 s). Org switch and sign-out clear everything.
 */
export async function clearApiCaches({ keepProjectList = false }: { keepProjectList?: boolean } = {}) {
  try {
    if (doLog()) console.log("clearApiCaches() clearing memory cache", { keepProjectList });
    if (keepProjectList) {
      for (const key of __apiMemoryCache.keys()) {
        if (!isProjectListCacheKey(key)) __apiMemoryCache.delete(key);
      }
    } else {
      __apiMemoryCache.clear();
    }
    __apiInFlight.clear();
  } catch (err) {
    if (doLog()) console.warn("clearApiCaches() memory clear failed", err);
  }
  try {
    if (keepProjectList) await idbDeleteWhere((key) => !isProjectListCacheKey(key));
    else await idbClearAll();
  } catch (err) {
    if (doLog()) console.warn("clearApiCaches() idb clear failed", err);
  }
}

// ======================================================================================|
//  MARK: Org/Global

/**
 * A7Y-3004: `cacheOptions` is optional and the existing zero-argument
 * behaviour is unchanged. The feature-status reader passes
 * `{ cache: { strategy: "network-only" } }` so a status flip in admin is
 * visible on the next hard reload rather than up to 30 minutes later — the
 * default GET policy for this endpoint is `swr` with a 30-minute IndexedDB
 * persist window.
 */
export async function apiPullGlobalConfig(
  userToken,
  cacheOptions: { cache?: CachePolicy } | undefined = undefined,
) {
  if (doLog()) console.log("apiPullGlobalConfig()");
  return await makeApiRequest("", "GET", userToken, null, null, cacheOptions);
}

export async function apiDeleteAccount(userToken) {
  if (doLog()) console.log("apiDeleteAccount()");
  return await makeApiRequest(`${API_ROOT_PATHS.org}`, "DELETE", userToken);
}

// ======================================================================================|
//  MARK: Projects

export async function apiCreateProject(userToken, projectData) {
  if (doLog()) console.log("apiCreateProject()");
  return await makeApiRequest(API_ROOT_PATHS.project, "POST", userToken, projectData);
}

export async function apiPullProjectList(userToken, skipCache = false) {
  try {
    if (doLog()) console.log("apiPullProjectList() skipCache:", skipCache);
    const cacheOptions = skipCache ? { cache: { strategy: "network-only" as const } } : undefined;
    const data = await makeApiRequest(API_ROOT_PATHS.project, "GET", userToken, null, null, cacheOptions);
    return data;
  } catch (error) {
    console.error(`apiPullProjectList(): ${error.message}`, error);
    return false;
  }
}

export async function apiUpdateProject(userToken, projectData) {
  if (doLog()) console.log("apiUpdateProject()");
  return await makeApiRequest(
    `${API_ROOT_PATHS.project}/${projectData.id}`,
    "PATCH",
    userToken,
    projectData
  );
}

/**
 * A7Y-1367 — Refresh the anchored app brief from the current live listing.
 * Synchronous on the server side; webapp blocks on this until the brief is
 * regenerated and persisted on the project doc.
 */
export async function apiRefreshAnchoredBrief(userToken, projectId) {
  if (doLog()) console.log("apiRefreshAnchoredBrief()");
  return await makeApiRequest(
    `${API_ROOT_PATHS.project}/${projectId}/anchored-brief/refresh`,
    "POST",
    userToken
  );
}

export async function apiAddAppEntry(
  userToken,
  projectId,
  payload: { storeId: string; appId: string; regionId: string; languageId: string }
) {
  if (doLog()) console.log("apiAddAppEntry()");
  return await makeApiRequest(
    `${API_ROOT_PATHS.project}/${projectId}/add-app-entry`,
    "POST",
    userToken,
    payload
  );
}

export async function apiUpdateProjectTargetLocales(
  userToken,
  projectId,
  targetLocales,
  localeHints
) {
  if (doLog()) console.log("apiUpdateProjectTargetLocales()");
  return await makeApiRequest(
    `${API_ROOT_PATHS.project}/${projectId}/target-locales`,
    "PATCH",
    userToken,
    { targetLocales, localeHints }
  );
}

export async function apiDeleteProject(userToken, projectId) {
  if (doLog()) console.log("apiDeleteProject()");
  return await makeApiRequest(
    `${API_ROOT_PATHS.project}/${projectId}`,
    "DELETE",
    userToken
  );
}

// ======================================================================================|
//  MARK: Website Sources (A7Y-800)

export type ValidateWebsiteSourceResponse = {
  url: string;
  status: WebsiteSourceStatus;
  qualityScore: number;
  reason?: string;
  lastValidatedAt: number;
};

/**
 * Validate a candidate website-source URL. Calls
 * `POST project/:projectId/website-sources/validate` on a7y-api.
 *
 * Returns the validation result on success or an ApiFailure on error. The
 * result includes the canonical URL, status, qualityScore, optional reason,
 * and the validation timestamp.
 */
export async function apiValidateWebsiteSourceUrl(
  userToken: string | null | undefined,
  projectId: string,
  url: string,
): Promise<ApiResult<ValidateWebsiteSourceResponse>> {
  if (doLog()) console.log("apiValidateWebsiteSourceUrl()", projectId, url);
  return (await makeApiRequest(
    `${API_ROOT_PATHS.project}/${projectId}/website-sources/validate`,
    "POST",
    userToken,
    { url },
  )) as ApiResult<ValidateWebsiteSourceResponse>;
}

/**
 * A7Y-800 (async refactor): the rediscover endpoint is now fire-and-forget.
 * The Worker returns 202 immediately after flipping
 * `websiteSourcesDiscoveryStatus` to `'in_progress'` and triggering the
 * Firebase function in the background; the webapp observes status updates on
 * the project doc to know when discovery is done.
 *
 * On 409 (conflict — discovery already in progress) `makeApiRequest` returns
 * the standard `ApiFailure` shape with `error.message` populated.
 */
export type RediscoverWebsiteSourcesAcceptResponse = {
  accepted: boolean;
  status: "in_progress";
  startedAt: number;
};

/**
 * Trigger website-source rediscovery for a project. Calls
 * `POST project/:projectId/website-sources/rediscover` on a7y-api, which
 * proxies the request to the `rediscoverWebsiteSources` Firebase function via
 * sxs-key + TOTP (same pattern as `triggerAuditFunction`). Keeping Clerk auth
 * at the api layer means the discovery function stays internal.
 *
 * Returns 202 Accepted on the happy path. The caller should observe
 * `currentProject.websiteSourcesDiscoveryStatus` (set by the Firebase
 * function) to detect completion. A 409 response means discovery is already
 * running for this project; surface the conflict message to the user.
 */
export async function apiRediscoverWebsiteSources(
  userToken: string | null | undefined,
  projectId: string,
): Promise<ApiResult<RediscoverWebsiteSourcesAcceptResponse>> {
  if (doLog()) console.log("apiRediscoverWebsiteSources()", projectId);
  const result = (await makeApiRequest(
    `${API_ROOT_PATHS.project}/${projectId}/website-sources/rediscover`,
    "POST",
    userToken,
    {},
  )) as ApiResult<RediscoverWebsiteSourcesAcceptResponse>;
  return result;
}

export async function apiGetProject(userToken, projectId) {
  if (doLog()) console.log("apiGetProject()");
  return await makeApiRequest(`${API_ROOT_PATHS.project}/${projectId}`, "GET", userToken);
}

export async function apiGetProjectMetadata(userToken, projectId) {
  if (doLog()) console.log("apiGetProjectMetadata()");
  return await makeApiRequest(
    `${API_ROOT_PATHS.project}/${projectId}`,
    "GET",
    userToken,
    null,
    { lite: "true" },
    { cache: { strategy: "network-only" } }
  );
}

// ======================================================================================|
//  MARK: Feature interest (A7Y-3046)

/**
 * GET the caller's own registration plus the display-safe waiting count.
 *
 * `network-only`: the default GET policy is `swr` with a 30-minute IndexedDB
 * persist window, which would keep showing a stale "We'll email you" state
 * after the user registered on another device — or, worse, keep offering the
 * button to someone already on the list.
 *
 * `waitingCount` is `null` below the server-side visibility threshold; the
 * client applies no threshold of its own (D6).
 */
export async function apiGetFeatureInterest(userToken, featureKey: string) {
  if (doLog()) console.log("apiGetFeatureInterest()", featureKey);
  return await makeApiRequest(
    `${API_ROOT_PATHS.featureInterest}/${featureKey}`,
    "GET",
    userToken,
    null,
    null,
    { cache: { strategy: "network-only" } },
  );
}

/**
 * Register the caller on a feature's waiting list. Idempotent server-side —
 * the document id is `${env}__${userId}__${featureKey}`, so a repeat press is
 * an upsert on the same row (D4).
 *
 * No `invalidateCachesForMutation` branch is needed: the GET above is
 * `network-only` and so has nothing cached to bust.
 */
export async function apiRegisterFeatureInterest(userToken, featureKey: string) {
  if (doLog()) console.log("apiRegisterFeatureInterest()", featureKey);
  return await makeApiRequest(
    `${API_ROOT_PATHS.featureInterest}/${featureKey}`,
    "POST",
    userToken,
    {},
  );
}

// ======================================================================================|
//  MARK: Chrome extension pairing (A7Y-4165)

/**
 * Mints a pairing code for the caller's own org — the code the settings page
 * shows (`ABCD-EFGH`) or hands to the extension via the one-click handoff.
 * Clerk-authenticated; the extension itself never calls this (it redeems the
 * code directly against `/extension/pair/complete`, unauthenticated).
 */
export async function apiExtensionPairStart(
  userToken: string,
  body: ExtensionPairStartRequest,
): Promise<ApiResult<ExtensionPairStartResponse>> {
  if (doLog()) console.log("apiExtensionPairStart()", body.orgId);
  return (await makeApiRequest(
    `${API_ROOT_PATHS.extension}/pair/start`,
    "POST",
    userToken,
    body,
  )) as ApiResult<ExtensionPairStartResponse>;
}

/** Every extension install the caller has paired, for the Settings page's list. */
export async function apiListExtensionTokens(
  userToken: string,
): Promise<ApiResult<ExtensionTokenListResponse>> {
  if (doLog()) console.log("apiListExtensionTokens()");
  return (await makeApiRequest(
    `${API_ROOT_PATHS.extension}/tokens`,
    "GET",
    userToken,
    null,
    null,
    { cache: { strategy: "network-only" } },
  )) as ApiResult<ExtensionTokenListResponse>;
}

/** Revokes one of the caller's own extension tokens by its doc id. */
export async function apiRevokeExtensionToken(
  userToken: string,
  tokenId: string,
): Promise<ApiResult<undefined>> {
  if (doLog()) console.log("apiRevokeExtensionToken()", tokenId);
  return (await makeApiRequest(
    `${API_ROOT_PATHS.extension}/tokens/${tokenId}`,
    "DELETE",
    userToken,
  )) as ApiResult<undefined>;
}

export async function apiCreateSavedItem(
  userToken: string,
  projectId: string,
  request: SavedItemCreateRequest,
) {
  if (doLog()) console.log("apiCreateSavedItem()", projectId);
  return await makeApiRequest(
    `${API_ROOT_PATHS.project}/${projectId}/saved_items`,
    "POST",
    userToken,
    request,
  );
}

export async function apiListSavedItems(
  userToken: string,
  projectId: string,
  query: SavedItemListQuery = {},
) {
  if (doLog()) console.log("apiListSavedItems()", projectId, query);
  const queryParams: Record<string, string | null | undefined> = {
    kind: query.kind,
    status: query.status,
    savedByUserId: query.savedByUserId,
    from: query.from,
    to: query.to,
  };
  return await makeApiRequest(
    `${API_ROOT_PATHS.project}/${projectId}/saved_items`,
    "GET",
    userToken,
    null,
    queryParams,
    { cache: { strategy: "network-only" } },
  );
}

export async function apiUpdateSavedItem(
  userToken: string,
  projectId: string,
  itemId: string,
  request: SavedItemUpdateRequest,
) {
  if (doLog()) console.log("apiUpdateSavedItem()", projectId, itemId);
  return await makeApiRequest(
    `${API_ROOT_PATHS.project}/${projectId}/saved_items/${itemId}`,
    "PATCH",
    userToken,
    request,
  );
}

export async function apiDeleteSavedItem(
  userToken: string,
  projectId: string,
  itemId: string,
) {
  if (doLog()) console.log("apiDeleteSavedItem()", projectId, itemId);
  return await makeApiRequest(
    `${API_ROOT_PATHS.project}/${projectId}/saved_items/${itemId}`,
    "DELETE",
    userToken,
  );
}

export async function apiCreateDismissalRule(
  userToken: string,
  projectId: string,
  request: DismissalRuleCreateRequest,
) {
  if (doLog()) console.log("apiCreateDismissalRule()", projectId);
  return await makeApiRequest(
    `${API_ROOT_PATHS.project}/${projectId}/dismissal_rules`,
    "POST",
    userToken,
    request,
  );
}

export async function apiListDismissalRules(
  userToken: string,
  projectId: string,
  query: DismissalRuleListQuery = {},
) {
  if (doLog()) console.log("apiListDismissalRules()", projectId, query);
  const queryParams: Record<string, string | null | undefined> = {
    kind: query.kind,
    engineType: query.engineType,
    reason_tag: query.reason_tag,
    from: query.from,
    to: query.to,
  };
  return await makeApiRequest(
    `${API_ROOT_PATHS.project}/${projectId}/dismissal_rules`,
    "GET",
    userToken,
    null,
    queryParams,
    { cache: { strategy: "network-only" } },
  );
}

export async function apiUpdateDismissalRule(
  userToken: string,
  projectId: string,
  ruleId: string,
  request: DismissalRuleUpdateRequest,
) {
  if (doLog()) console.log("apiUpdateDismissalRule()", projectId, ruleId);
  return await makeApiRequest(
    `${API_ROOT_PATHS.project}/${projectId}/dismissal_rules/${ruleId}`,
    "PATCH",
    userToken,
    request,
  );
}

export async function apiDeleteDismissalRule(
  userToken: string,
  projectId: string,
  ruleId: string,
) {
  if (doLog()) console.log("apiDeleteDismissalRule()", projectId, ruleId);
  return await makeApiRequest(
    `${API_ROOT_PATHS.project}/${projectId}/dismissal_rules/${ruleId}`,
    "DELETE",
    userToken,
  );
}

export async function apiGetProjectEntitlements(userToken, projectId) {
  if (doLog()) console.log("apiGetProjectEntitlements()");
  return await makeApiRequest(
    `${API_SUB_PATHS.project_billing}/entitlements/${projectId}`,
    "GET",
    userToken
  );
}

export async function apiListAssociatedAscApps(userToken, projectId) {
  if (doLog()) console.log("apiListAssociatedAscApps()");
  return await makeApiRequest(
    `${API_SUB_PATHS.project_associated_apps}/list/${projectId}`,
    "GET",
    userToken
  );
}

// ======================================================================================|
//  MARK: Competitors

export async function apiListCompetitors(userToken, projectId) {
  if (doLog()) console.log("apiListCompetitors() projectId", projectId);
  return await makeApiRequest(
    `${API_SUB_PATHS.project_competitors}/list/${projectId}`,
    "GET",
    userToken,
    null,
    null,
    { cache: { strategy: "network-only" } }
  );
}

export async function apiGetCompetitor(userToken, projectId, competitorId) {
  if (doLog()) console.log("apiGetCompetitor() projectId competitorId", projectId, competitorId);
  return await makeApiRequest(
    `${API_SUB_PATHS.project_competitors}/${projectId}/${competitorId}`,
    "GET",
    userToken
  );
}

export async function apiAddCompetitor(userToken, projectId, competitorData: { storeId: string; appId: string; note?: string }) {
  if (doLog()) console.log("apiAddCompetitor() projectId competitorData", projectId, competitorData);
  return await makeApiRequest(
    `${API_SUB_PATHS.project_competitors}/${projectId}`,
    "POST",
    userToken,
    competitorData
  );
}

export async function apiUpdateCompetitor(userToken, projectId, competitorId, updateData: { note?: string }) {
  if (doLog()) console.log("apiUpdateCompetitor() projectId competitorId updateData", projectId, competitorId, updateData);
  return await makeApiRequest(
    `${API_SUB_PATHS.project_competitors}/${projectId}/${competitorId}`,
    "PATCH",
    userToken,
    updateData
  );
}

export async function apiDeleteCompetitor(userToken, projectId, competitorId) {
  if (doLog()) console.log("apiDeleteCompetitor() projectId competitorId", projectId, competitorId);
  return await makeApiRequest(
    `${API_SUB_PATHS.project_competitors}/${projectId}/${competitorId}`,
    "DELETE",
    userToken
  );
}

// A7Y-5284 — apps the audit added that the customer removed from this
// project's audits. Each call resolves to the list as stored afterwards.
export async function apiListExcludedCompetitors(userToken, projectId) {
  if (doLog()) console.log("apiListExcludedCompetitors() projectId", projectId);
  return await makeApiRequest(
    `${API_SUB_PATHS.project_competitors}/excluded/${projectId}`,
    "GET",
    userToken,
    null,
    null,
    { cache: { strategy: "network-only" } }
  );
}

export async function apiExcludeCompetitor(
  userToken,
  projectId,
  competitor: { storeId: string; appId: string; name?: string; iconUrl?: string | null }
) {
  if (doLog()) console.log("apiExcludeCompetitor() projectId competitor", projectId, competitor);
  return await makeApiRequest(
    `${API_SUB_PATHS.project_competitors}/excluded/${projectId}`,
    "POST",
    userToken,
    competitor
  );
}

export async function apiRestoreExcludedCompetitor(userToken, projectId, storeId: string, appId: string) {
  if (doLog()) console.log("apiRestoreExcludedCompetitor() projectId storeId appId", projectId, storeId, appId);
  return await makeApiRequest(
    `${API_SUB_PATHS.project_competitors}/excluded/${projectId}/${encodeURIComponent(storeId)}/${encodeURIComponent(appId)}`,
    "DELETE",
    userToken
  );
}

export async function apiGetSuggestedCompetitors(userToken, projectId) {
  if (doLog()) console.log("apiGetSuggestedCompetitors() projectId", projectId);
  return await makeApiRequest(
    `${API_SUB_PATHS.project_competitors}/suggested/${projectId}`,
    "GET",
    userToken,
    null,
    null,
    { cache: { strategy: "network-only" } }
  );
}

export async function apiGetCompetitorActivity(
  userToken: string,
  projectId: string,
  /** A7Y-4676: a non-US storefront (two-letter country); absent or `us` reads the canonical captures. */
  storefront?: string,
) {
  if (doLog()) console.log("apiGetCompetitorActivity() projectId", projectId);
  const query = storefront ? `?storefront=${encodeURIComponent(storefront)}` : "";
  return await makeApiRequest(
    `${API_SUB_PATHS.project_competitors}/activity/${projectId}${query}`,
    "GET",
    userToken,
    null,
    null,
    { cache: { strategy: "network-only" } },
  );
}

// A7Y-5339 — the creatives-by-country grid. Mirrors the `/creatives` routes in
// a7y-api/src/routes/project_subroutes/project_competitors_route.ts.
export async function apiGetCompetitorCreatives(
  userToken: string,
  projectId: string,
  store: "appstore" | "playstore",
) {
  if (doLog()) console.log("apiGetCompetitorCreatives() projectId store", projectId, store);
  return await makeApiRequest(
    `${API_SUB_PATHS.project_competitors}/creatives/${projectId}?store=${store}`,
    "GET",
    userToken,
    null,
    null,
    { cache: { strategy: "network-only" } },
  );
}

/** Read and translate the next batch of unread competitor screenshots in one country. */
export async function apiReadCompetitorScreenshotText(
  userToken: string,
  projectId: string,
  body: { store: "appstore" | "playstore"; storefront: string },
) {
  if (doLog()) console.log("apiReadCompetitorScreenshotText() projectId body", projectId, body);
  return await makeApiRequest(
    `${API_SUB_PATHS.project_competitors}/creatives/${projectId}/read-text`,
    "POST",
    userToken,
    body,
  );
}

/** Tag or untag one app's creatives in one country as culturalized. */
export async function apiSetCreativeCulturalized(
  userToken: string,
  projectId: string,
  body: { store: "appstore" | "playstore"; appId: string; storefront: string; culturalized: boolean },
) {
  if (doLog()) console.log("apiSetCreativeCulturalized() projectId body", projectId, body);
  return await makeApiRequest(
    `${API_SUB_PATHS.project_competitors}/creatives/${projectId}/culturalized`,
    "PUT",
    userToken,
    body,
  );
}

// ======================================================================================|
//  MARK: User Reviews (A7Y-5859)
//  Mirrors a7y-api/src/routes/project_subroutes/project_reviews_route.ts.
//  `reviewId` is the AppReviews document id (`UserReviewRow.id`). Reads are
//  network-only: a stale list would hide a reply that was just posted.

/** Paged review list for one project; filters and cursor per `UserReviewsListParams`. */
export async function apiGetReviews(
  userToken,
  projectId: string,
  params: UserReviewsListParams = {},
): Promise<ApiResult<UserReviewsListResponse>> {
  if (doLog()) console.log("apiGetReviews() projectId", projectId, params);
  return (await makeApiRequest(
    `${API_SUB_PATHS.project_reviews}/${projectId}`,
    "GET",
    userToken,
    null,
    {
      store: params.store ?? null,
      rating: params.rating && params.rating.length > 0 ? params.rating.join(",") : null,
      replied: params.replied === undefined ? null : String(params.replied),
      territory: params.territory ?? null,
      language: params.language ?? null,
      appVersion: params.appVersion ?? null,
      limit: params.limit != null ? String(params.limit) : null,
      cursor: params.cursor ?? null,
    },
    { cache: { strategy: "network-only" } },
  )) as ApiResult<UserReviewsListResponse>;
}

/**
 * Page-open refresh. A throttled call (one per project per window; source: A7Y-5859 D15) answers
 * 200 with `throttled: true`; that is the normal steady state, not a failure.
 */
export async function apiRefreshReviews(
  userToken,
  projectId: string,
): Promise<ApiResult<UserReviewsRefreshResponse>> {
  if (doLog()) console.log("apiRefreshReviews() projectId", projectId);
  return (await makeApiRequest(
    `${API_SUB_PATHS.project_reviews}/${projectId}/refresh`,
    "POST",
    userToken,
  )) as ApiResult<UserReviewsRefreshResponse>;
}

/** Posts (or overwrites) the developer reply on the review's store. */
export async function apiReplyToReview(
  userToken,
  projectId: string,
  reviewId: string,
  body: ReplyToReviewRequest,
): Promise<ApiResult<ReplyToReviewResponse>> {
  if (doLog()) console.log("apiReplyToReview() projectId", projectId, "reviewId", reviewId);
  return (await makeApiRequest(
    `${API_SUB_PATHS.project_reviews}/${projectId}/${reviewId}/reply`,
    "POST",
    userToken,
    body,
  )) as ApiResult<ReplyToReviewResponse>;
}

/** Deletes an App Store reply (Google Play has no delete). */
export async function apiDeleteReviewReply(
  userToken,
  projectId: string,
  reviewId: string,
): Promise<ApiResult<ReplyToReviewResponse>> {
  if (doLog()) console.log("apiDeleteReviewReply() projectId", projectId, "reviewId", reviewId);
  return (await makeApiRequest(
    `${API_SUB_PATHS.project_reviews}/${projectId}/${reviewId}/reply`,
    "DELETE",
    userToken,
  )) as ApiResult<ReplyToReviewResponse>;
}

/** One reply draft for the composer. Nothing is posted. */
export async function apiGenerateReviewReply(
  userToken,
  projectId: string,
  reviewId: string,
): Promise<ApiResult<GenerateReviewReplyResponse>> {
  if (doLog()) console.log("apiGenerateReviewReply() projectId", projectId, "reviewId", reviewId);
  return (await makeApiRequest(
    `${API_SUB_PATHS.project_reviews}/${projectId}/${reviewId}/generate-reply`,
    "POST",
    userToken,
  )) as ApiResult<GenerateReviewReplyResponse>;
}

// ======================================================================================|
//  MARK: Listing History (A7Y-4207)
//  Reads for the vertical listing-history timeline. Mirrors
//  a7y-api/src/routes/project_subroutes/project_listing_history_route.ts
//  exactly — query-param names, paths, and response shapes.

export interface ListingHistoryRecordsParams {
  store: "appstore" | "playstore";
  appId: string;
  storefront: string;
  locale: string;
  limit?: number;
  before?: string;
}

export async function apiGetListingHistoryRecords(
  userToken: string,
  projectId: string,
  params: ListingHistoryRecordsParams,
) {
  if (doLog()) console.log("apiGetListingHistoryRecords() projectId", projectId, params);
  const query = new URLSearchParams();
  query.set("store", params.store);
  query.set("appId", params.appId);
  query.set("storefront", params.storefront);
  query.set("locale", params.locale);
  if (params.limit != null) query.set("limit", String(params.limit));
  if (params.before) query.set("before", params.before);
  return await makeApiRequest(
    `${API_SUB_PATHS.project_listing_history}/records/${projectId}?${query.toString()}`,
    "GET",
    userToken,
    null,
    null,
    { cache: { strategy: "network-only" } },
  );
}

export interface ListingHistoryChangesetsParams {
  store: "appstore" | "playstore";
  appId: string;
  storefront?: string;
  locale?: string;
  limit?: number;
  before?: string;
}

export async function apiGetListingHistoryChangesets(
  userToken: string,
  projectId: string,
  params: ListingHistoryChangesetsParams,
) {
  if (doLog()) console.log("apiGetListingHistoryChangesets() projectId", projectId, params);
  const query = new URLSearchParams();
  query.set("store", params.store);
  query.set("appId", params.appId);
  if (params.storefront) query.set("storefront", params.storefront);
  if (params.locale) query.set("locale", params.locale);
  if (params.limit != null) query.set("limit", String(params.limit));
  if (params.before) query.set("before", params.before);
  return await makeApiRequest(
    `${API_SUB_PATHS.project_listing_history}/changesets/${projectId}?${query.toString()}`,
    "GET",
    userToken,
    null,
    null,
    { cache: { strategy: "network-only" } },
  );
}

// ======================================================================================|
//  MARK: App Events (A7Y-2881)

export async function apiGetOwnAppEvents(userToken: string, projectId: string) {
  if (doLog()) console.log("apiGetOwnAppEvents() projectId", projectId);
  return await makeApiRequest(
    `${API_SUB_PATHS.project_app_events}/own/${projectId}`,
    "GET",
    userToken,
    null,
    null,
    { cache: { strategy: "network-only" } },
  );
}

/**
 * A7Y-3689 — persisted ranked-marketing-moments recommendations for the events
 * dashboard rail. A7Y-4543: per store — each store's surface is ranked from
 * that store's own inputs.
 */
export async function apiGetMomentRecommendations(
  userToken: string,
  projectId: string,
  store: "appstore" | "playstore",
) {
  if (doLog()) console.log("apiGetMomentRecommendations() projectId", projectId, store);
  return await makeApiRequest(
    `${API_SUB_PATHS.project_app_events}/recommendations/${projectId}?store=${store}`,
    "GET",
    userToken,
    null,
    null,
    { cache: { strategy: "network-only" } },
  );
}

export async function apiGetOwnAppEventDetail(userToken: string, projectId: string, ascEventId: string) {
  if (doLog()) console.log("apiGetOwnAppEventDetail() projectId ascEventId", projectId, ascEventId);
  return await makeApiRequest(
    `${API_SUB_PATHS.project_app_events}/own/${projectId}/${ascEventId}`,
    "GET",
    userToken,
    null,
    null,
    { cache: { strategy: "network-only" } },
  );
}

export async function apiGetCompetitorAppEvents(userToken: string, projectId: string) {
  if (doLog()) console.log("apiGetCompetitorAppEvents() projectId", projectId);
  return await makeApiRequest(
    `${API_SUB_PATHS.project_app_events}/competitors/${projectId}`,
    "GET",
    userToken,
    null,
    null,
    { cache: { strategy: "network-only" } },
  );
}

/** A7Y-3845: standing-count usage for the three in-app-events quotas. */
export async function apiGetAppEventsQuotas(userToken: string, projectId: string) {
  if (doLog()) console.log("apiGetAppEventsQuotas() projectId", projectId);
  return await makeApiRequest(
    `${API_SUB_PATHS.project_app_events}/quotas/${projectId}`,
    "GET",
    userToken,
    null,
    null,
    { cache: { strategy: "network-only" } },
  );
}

export async function apiGetPortfolioAppEvents(userToken: string) {
  if (doLog()) console.log("apiGetPortfolioAppEvents()");
  return await makeApiRequest(
    `${API_SUB_PATHS.project_app_events}/portfolio`,
    "GET",
    userToken,
    null,
    null,
    { cache: { strategy: "network-only" } },
  );
}

// ======================================================================================|
//  MARK: App Event drafts — save to App Store Connect (A7Y-2895)

/**
 * Kicks off the async ASC write via the existing listing-publish seam.
 * Returns `{ projectId, publishId }` — stream progress with
 * `connectPublishSSE`/`usePublishProgress` against the returned `publishId`,
 * never a synchronous result.
 */
export async function apiSaveAppEventDraft(
  userToken: string,
  projectId: string,
  body: SaveAppEventDraftRequest,
) {
  if (doLog()) console.log("apiSaveAppEventDraft() projectId", projectId);
  return await makeApiRequest(
    `${API_SUB_PATHS.project_app_events}/own/${projectId}/save`,
    "POST",
    userToken,
    body,
  );
}

/** Uploads one event-card/details-page image into `apg-public`; returns its public URL. */
export async function apiUploadAppEventMedia(
  userToken: string,
  projectId: string,
  body: UploadAppEventMediaRequest,
) {
  if (doLog()) console.log("apiUploadAppEventMedia() projectId", projectId);
  return await makeApiRequest(
    `${API_SUB_PATHS.project_app_events}/own/${projectId}/media`,
    "POST",
    userToken,
    body,
  );
}

// ======================================================================================|
//  MARK: App Event submission — App Review / Featuring nomination (A7Y-2900)

/**
 * Kicks off the async App Review submission via the listing-publish seam
 * (same shape as `apiSaveAppEventDraft` above). Returns
 * `AppEventSubmissionKickoffResponse` — stream progress with
 * `connectPublishSSE`/`usePublishProgress` against the returned `publishId`.
 */
export async function apiSubmitAppEventForReview(
  userToken: string,
  projectId: string,
  ascEventId: string,
  body: SubmitAppEventForReviewRequest,
) {
  if (doLog()) console.log("apiSubmitAppEventForReview() projectId", projectId, "ascEventId", ascEventId);
  return await makeApiRequest(
    `${API_SUB_PATHS.project_app_events}/own/${projectId}/${ascEventId}/submit-review`,
    "POST",
    userToken,
    body,
  );
}

/**
 * Kicks off the async withdrawal of an in-flight App Review submission
 * (A7Y-2901) via the same listing-publish seam — the ASC cancel PATCH runs
 * inside the submission engine, not synchronously here. Same async shape as
 * `apiSubmitAppEventForReview`: returns `AppEventSubmissionKickoffResponse`,
 * and progress/outcome is observed via the existing Activity-detail poll
 * (D6), not `usePublishProgress`/SSE.
 */
export async function apiWithdrawAppEventReview(
  userToken: string,
  projectId: string,
  ascEventId: string,
  body: WithdrawAppEventReviewRequest,
) {
  if (doLog()) console.log("apiWithdrawAppEventReview() projectId", projectId, "ascEventId", ascEventId);
  return await makeApiRequest(
    `${API_SUB_PATHS.project_app_events}/own/${projectId}/${ascEventId}/withdraw-review`,
    "POST",
    userToken,
    body,
  );
}

/**
 * Kicks off the async draft deletion — App Store Connect first, then our own
 * store (TPF, 2026-09-15). Same async shape as `apiWithdrawAppEventReview`:
 * returns `AppEventSubmissionKickoffResponse`, with progress observed through
 * the Activity-detail poll rather than SSE.
 */
export async function apiDeleteAppEventDraft(
  userToken: string,
  projectId: string,
  ascEventId: string,
  body: DeleteAppEventDraftRequest,
) {
  if (doLog()) console.log("apiDeleteAppEventDraft() projectId", projectId, "ascEventId", ascEventId);
  return await makeApiRequest(
    `${API_SUB_PATHS.project_app_events}/own/${projectId}/${ascEventId}/delete`,
    "POST",
    userToken,
    body,
  );
}

/** Kicks off the async Featuring-nomination submission. Same async shape as `apiSubmitAppEventForReview`. */
export async function apiSubmitFeaturingNomination(
  userToken: string,
  projectId: string,
  ascEventId: string,
  body: SubmitFeaturingNominationRequest,
) {
  if (doLog()) console.log("apiSubmitFeaturingNomination() projectId", projectId, "ascEventId", ascEventId);
  return await makeApiRequest(
    `${API_SUB_PATHS.project_app_events}/own/${projectId}/${ascEventId}/submit-nomination`,
    "POST",
    userToken,
    body,
  );
}

/**
 * Live read of this project's currently-open App Store Connect review
 * submissions (D16) — used by the Send screen so a blocked Send (items-only
 * slot taken) is explained before the click, not after.
 */
export async function apiGetOpenReviewSubmissions(userToken: string, projectId: string) {
  if (doLog()) console.log("apiGetOpenReviewSubmissions() projectId", projectId);
  return await makeApiRequest(
    `${API_SUB_PATHS.project_app_events}/own/${projectId}/review-submissions`,
    "GET",
    userToken,
    null,
    null,
    { cache: { strategy: "network-only" } },
  );
}

// ======================================================================================|
//  MARK: Featuring watch (A7Y-2902). The Google Play featuring self-report is per item (A7Y-5046) — see apiSetGooglePromoItemFeaturing.

export async function apiGetFeaturingWatch(userToken: string, projectId: string) {
  if (doLog()) console.log("apiGetFeaturingWatch() projectId", projectId);
  return await makeApiRequest(
    `${API_SUB_PATHS.project_app_events}/featuring/${projectId}`,
    "GET",
    userToken,
    null,
    null,
    { cache: { strategy: "network-only" } },
  );
}

/** Where Apple's Today tab and Apps/Games collections showed the project's own and competitor apps (A7Y-4692). */
export async function apiGetAppleFeaturingObservations(userToken: string, projectId: string) {
  if (doLog()) console.log("apiGetAppleFeaturingObservations() projectId", projectId);
  return await makeApiRequest(
    `${API_SUB_PATHS.project_app_events}/featuring/observations/${projectId}`,
    "GET",
    userToken,
    null,
    null,
    { cache: { strategy: "network-only" } },
  );
}

/**
 * Exactly one of `placementId` / `ascEventId` is meaningful to the route — it
 * 400s on neither and on both. The caller decides; this only serializes.
 */
export async function apiGetFeaturingCorrelation(
  userToken: string,
  projectId: string,
  params: { placementId?: string; ascEventId?: string; metric?: string },
) {
  const query = new URLSearchParams();
  if (params.placementId !== undefined) query.set("placementId", params.placementId);
  if (params.ascEventId !== undefined) query.set("ascEventId", params.ascEventId);
  if (params.metric !== undefined) query.set("metric", params.metric);
  const suffix = query.toString();
  if (doLog()) console.log("apiGetFeaturingCorrelation() projectId params", projectId, suffix);
  return await makeApiRequest(
    `${API_SUB_PATHS.project_app_events}/featuring/${projectId}/correlation${suffix === "" ? "" : `?${suffix}`}`,
    "GET",
    userToken,
    null,
    null,
    { cache: { strategy: "network-only" } },
  );
}

import type {
  SaveGooglePromoItemRequest,
  SetGooglePromoItemFeaturingRequest,
  SetGooglePromoItemStatusRequest,
  UploadGooglePromoAnimationRequest,
  UploadGooglePromoImageRequest,
} from "@a7y/types";

// ======================================================================================|
//  MARK: Google Play promotional-content items (A7Y-4082)
//
//  The promotional-content item is its own persisted entity — not a variant of
//  an App Store in-app event. `GooglePromoItemView` carries the server-derived
//  `phase`, `phaseGroup` and `validation` alongside the stored item, so no
//  caller recomputes readiness or re-derives which tab an item belongs to.

export async function apiGetGooglePromoItems(userToken: string, projectId: string) {
  if (doLog()) console.log("apiGetGooglePromoItems() projectId", projectId);
  return await makeApiRequest(
    `${API_SUB_PATHS.project_app_events}/google-play/promo/${projectId}`,
    "GET",
    userToken,
    null,
    null,
    { cache: { strategy: "network-only" } },
  );
}

export async function apiGetGooglePromoItem(
  userToken: string,
  projectId: string,
  itemId: string,
) {
  if (doLog()) console.log("apiGetGooglePromoItem() projectId", projectId, "itemId", itemId);
  return await makeApiRequest(
    `${API_SUB_PATHS.project_app_events}/google-play/promo/${projectId}/${itemId}`,
    "GET",
    userToken,
    null,
    null,
    { cache: { strategy: "network-only" } },
  );
}

/** `itemId: null` creates; a string updates that item. */
export async function apiSaveGooglePromoItem(
  userToken: string,
  projectId: string,
  body: SaveGooglePromoItemRequest,
) {
  if (doLog()) console.log("apiSaveGooglePromoItem() projectId", projectId, "itemId", body.itemId);
  return await makeApiRequest(
    `${API_SUB_PATHS.project_app_events}/google-play/promo/${projectId}`,
    "POST",
    userToken,
    body,
  );
}

/** The user's self-report that they pasted the item into Play Console, or withdrew it. */
export async function apiSetGooglePromoItemStatus(
  userToken: string,
  projectId: string,
  itemId: string,
  body: SetGooglePromoItemStatusRequest,
) {
  if (doLog()) console.log("apiSetGooglePromoItemStatus() itemId", itemId, "status", body.status);
  return await makeApiRequest(
    `${API_SUB_PATHS.project_app_events}/google-play/promo/${projectId}/${itemId}/status`,
    "POST",
    userToken,
    body,
  );
}

/**
 * The user's self-report that a featuring request was submitted for this item
 * (A7Y-5046). A self-report, never a measurement; re-posting overwrites.
 */
export async function apiSetGooglePromoItemFeaturing(
  userToken: string,
  projectId: string,
  itemId: string,
  body: SetGooglePromoItemFeaturingRequest,
) {
  if (doLog()) console.log("apiSetGooglePromoItemFeaturing() itemId", itemId, "submitted", body.submitted);
  return await makeApiRequest(
    `${API_SUB_PATHS.project_app_events}/google-play/promo/${projectId}/${itemId}/featuring`,
    "POST",
    userToken,
    body,
  );
}

export async function apiDeleteGooglePromoItem(
  userToken: string,
  projectId: string,
  itemId: string,
) {
  if (doLog()) console.log("apiDeleteGooglePromoItem() projectId", projectId, "itemId", itemId);
  return await makeApiRequest(
    `${API_SUB_PATHS.project_app_events}/google-play/promo/${projectId}/${itemId}/delete`,
    "POST",
    userToken,
    {},
  );
}

/**
 * Upload one primary image. PNG only — the Google Play spec is 1920x1080 PNG,
 * so a JPEG is refused here rather than stored and reported as wrong on every
 * package build. The response's `image` is the record to put into the draft's
 * `images` array, with `detectedText` already filled by the OCR pass;
 * `detectionRan: false` means that pass did not run, and the empty
 * `detectedText` then means "nobody checked" rather than "no text".
 */
export async function apiUploadGooglePromoImage(
  userToken: string,
  projectId: string,
  body: UploadGooglePromoImageRequest,
) {
  if (doLog()) console.log("apiUploadGooglePromoImage() projectId", projectId, "locale", body.locale);
  return await makeApiRequest(
    `${API_SUB_PATHS.project_app_events}/google-play/promo/${projectId}/image`,
    "POST",
    userToken,
    body,
  );
}

/**
 * Upload one locale's Lottie animation (A7Y-4055) as the file's JSON text.
 * The route refuses what is not a readable Lottie document; Google's limits
 * come back as findings on the item, not as a refusal here.
 */
export async function apiUploadGooglePromoAnimation(
  userToken: string,
  projectId: string,
  body: UploadGooglePromoAnimationRequest,
) {
  if (doLog()) console.log("apiUploadGooglePromoAnimation() projectId", projectId, "locale", body.locale);
  return await makeApiRequest(
    `${API_SUB_PATHS.project_app_events}/google-play/promo/${projectId}/animation`,
    "POST",
    userToken,
    body,
  );
}

// ======================================================================================|
//  MARK: Event Localizations (A7Y-2894)

export async function apiGenerateEventLocalizations(
  userToken: string,
  projectId: string,
  ascEventId: string,
  body: GenerateEventLocalizationsRequest,
) {
  if (doLog()) console.log("apiGenerateEventLocalizations() projectId ascEventId", projectId, ascEventId);
  return await makeApiRequest(
    `${API_SUB_PATHS.project_app_events}/own/${projectId}/${ascEventId}/localizations/generate`,
    "POST",
    userToken,
    body,
  );
}

export async function apiSetEventLocalizationState(
  userToken: string,
  projectId: string,
  ascEventId: string,
  body: EventLocalizationStateRequest,
) {
  if (doLog()) console.log("apiSetEventLocalizationState() projectId ascEventId", projectId, ascEventId);
  return await makeApiRequest(
    `${API_SUB_PATHS.project_app_events}/own/${projectId}/${ascEventId}/localizations/state`,
    "POST",
    userToken,
    body,
  );
}

// ======================================================================================|
//  MARK: AI Discovery prompt set (A7Y-5533)

export async function apiGetDiscoveryPromptSet(userToken, projectId) {
  if (doLog()) console.log("apiGetDiscoveryPromptSet() projectId", projectId);
  return await makeApiRequest(
    `${API_SUB_PATHS.project_discovery_prompts}/${projectId}`,
    "GET",
    userToken
  );
}

/** `action` is a `DiscoveryPromptSetAction`; `rerun` asks to spend one of today's save-triggered re-runs. */
export async function apiUpdateDiscoveryPromptSet(userToken, projectId, action, rerun = false) {
  if (doLog()) console.log("apiUpdateDiscoveryPromptSet() projectId", projectId, action?.type);
  return await makeApiRequest(
    `${API_SUB_PATHS.project_discovery_prompts}/${projectId}`,
    "POST",
    userToken,
    { action, rerun }
  );
}

// ======================================================================================|
//  MARK: Audit Schedule

export async function apiGetAuditSchedule(userToken, projectId) {
  if (doLog()) console.log("apiGetAuditSchedule() projectId", projectId);
  return await makeApiRequest(
    `${API_SUB_PATHS.project_audit_schedule}/${projectId}`,
    "GET",
    userToken
  );
}

export async function apiUpdateAuditSchedule(userToken, projectId, scheduleConfig) {
  if (doLog()) console.log("apiUpdateAuditSchedule() projectId", projectId);
  return await makeApiRequest(
    `${API_SUB_PATHS.project_audit_schedule}/${projectId}`,
    "PUT",
    userToken,
    scheduleConfig
  );
}

export async function apiDeleteAuditSchedule(userToken, projectId) {
  if (doLog()) console.log("apiDeleteAuditSchedule() projectId", projectId);
  return await makeApiRequest(
    `${API_SUB_PATHS.project_audit_schedule}/${projectId}`,
    "DELETE",
    userToken
  );
}

// ======================================================================================|
//  MARK: ASO Audit Timeline

/**
 * Fetch the audit score timeline for an app.
 * Returns score progression points, timeline events, and deltas.
 */
export async function apiGetAuditTimeline(
  userToken,
  request: {
    appIdentifier: string;
    projectId: string;
    storeId: "appstore" | "playstore";
    /**
     * A7Y-1388: optional locale filter. When omitted the API defaults to the
     * project's primary locale to avoid cross-locale fingerprint diff
     * pollution.
     */
    locale?: string;
    limit?: number;
    startDate?: string;
  }
) {
  if (doLog())
    console.log("apiGetAuditTimeline() request", request);
  return await makeApiRequest(
    "aso/listing-audit/timeline",
    "POST",
    userToken,
    request,
    null,
    { cache: { strategy: "network-only" } }
  );
}

// ======================================================================================|
//  MARK: Project

export async function apiRotateProjectApiKey(userToken, projectId) {
  if (doLog()) console.log("apiRotateProjectApiKey()");
  return await makeApiRequest(
    `${API_ROOT_PATHS.project}/${projectId}/rotate-key`,
    "POST",
    userToken
  );
}

// A7Y-4614: Figma plugin pairing tokens. `apiIssuePluginToken` issues (or
// renews) the caller's token and is the only response that carries it.
export async function apiListPluginTokens(
  userToken,
  projectId: string
): Promise<ApiResult<{ tokens: PluginTokenSummary[] }>> {
  return (await makeApiRequest(
    `${API_ROOT_PATHS.project}/${projectId}/plugin-tokens`,
    "GET",
    userToken
  )) as ApiResult<{ tokens: PluginTokenSummary[] }>;
}

export async function apiIssuePluginToken(
  userToken,
  projectId: string
): Promise<ApiResult<{ token: string; pluginToken: PluginTokenSummary }>> {
  return (await makeApiRequest(
    `${API_ROOT_PATHS.project}/${projectId}/plugin-tokens`,
    "POST",
    userToken
  )) as ApiResult<{ token: string; pluginToken: PluginTokenSummary }>;
}

export async function apiRevokePluginToken(userToken, projectId: string, tokenId: string) {
  return await makeApiRequest(
    `${API_ROOT_PATHS.project}/${projectId}/plugin-tokens/${tokenId}`,
    "DELETE",
    userToken
  );
}

export async function apiConfirmUserAccessToProject(userToken, projectId) {
  if (doLog()) console.log("apiConfirmUserAccessToProject()");
  return await makeApiRequest(
    `${API_ROOT_PATHS.project}/confirm-access/${projectId}`,
    "GET",
    userToken
  );
}

// export async function apiVerifyAppId(userToken, storeId, appId) {
//   if (doLog()) console.log("apiVerifyAppId() storeId appId", storeId, appId);
//   return await makeApiRequest(
//     `${API_ROOT_PATHS.project}/store-info/${storeId}/${appId}`,
//     "GET",
//     userToken
//   );
// }

export async function apiStoreEnrich(
  userToken,
  storeId,
  appId,
  regionId?: string | null,
  languageId?: string | null
) {
  if (doLog())
    console.log(
      "apiStoreEnrich() storeId appId regionId languageId",
      storeId,
      appId,
      regionId,
      languageId
    );
  return await makeApiRequest(
    `${API_ROOT_PATHS.project}/store-enrich`,
    "POST",
    userToken,
    {
      storeId,
      appId,
      ...(regionId ? { regionId } : {}),
      ...(languageId ? { languageId } : {}),
    }
  );
}

export async function apiGetLiveListing(
  userToken,
  storeId,
  appId,
  region?: string | null,
  projectId?: string | null,
  /** A7Y-506: Play Store language override (e.g. "es"). */
  languageId?: string | null
) {
  if (doLog())
    console.log(
      "apiGetLiveListing() storeId appId region projectId languageId",
      storeId,
      appId,
      region,
      projectId,
      languageId
    );
  // A7Y-1227: client param kept as `region` for caller convenience, but the
  // wire body field is `regionId` to align with the rest of the locale API.
  return await makeApiRequest(
    `${API_ROOT_PATHS.project}/live-listing`,
    "POST",
    userToken,
    {
      storeId,
      appId,
      regionId: region,
      projectId,
      ...(languageId ? { languageId } : {}),
    },
    null,
    { cache: { strategy: "swr", memoryTtlMs: 5 * 60 * 1000, persistMs: 30 * 60 * 1000 } }
  );
}

export async function apiGetScreenshots(userToken, appStoreId, playStoreId) {
  if (doLog())
    console.log("apiGetScreenshots() appStoreId playStoreId", appStoreId, playStoreId);
  appStoreId = appStoreId?.toString();
  return await makeApiRequest(
    `${API_ROOT_PATHS.project}/get-store-screens`,
    "POST",
    userToken,
    {
      appStoreId,
      playStoreId,
    }
  );
}

// ======================================================================================|
//  MARK: Strings

// export async function apiPullStrings(userToken, projectId, stringType) {
//   // if (doLog())
//   //   console.log("apiPullStrings() projectId stringType", projectId, stringType);
//   return await makeApiRequest(
//     `${API_SUB_PATHS.project_strings}/${projectId}/${stringType}`,
//     "GET",
//     userToken
//   );
// }

export async function apiPullStrings(
  userToken,
  projectId,
  stringType,
  skipCache = false
) {
  if (doLog())
    console.log("apiPullStrings() projectId stringType", projectId, stringType);
  const cacheOptions = skipCache ? { cache: { strategy: "network-only" as const } } : undefined;
  return await makeApiRequest(
    `${API_SUB_PATHS.project_strings}/${projectId}/${stringType}`,
    "GET",
    userToken,
    null,
    null,
    cacheOptions
  );
}

export async function apiCreateString(userToken, projectId, stringData) {
  if (doLog())
    console.log("apiCreateString() projectId stringData", projectId, stringData);
  return await makeApiRequest(
    `${API_SUB_PATHS.project_strings}/${projectId}`,
    "POST",
    userToken,
    stringData
  );
}

export async function apiUpdateString(
  userToken,
  projectId,
  stringSysId,
  updateObj,
  performTranslation,
  stringType
) {
  if (doLog())
    console.log(
      "apiUpdateString() projectId stringSysId updateObj performTranslation stringType",
      projectId,
      stringSysId,
      updateObj,
      performTranslation,
      stringType
    );
  return await makeApiRequest(
    `${API_SUB_PATHS.project_strings}/${projectId}/${stringSysId}`,
    "PATCH",
    userToken,
    {
      ...updateObj,
      stringType,
      performTranslation,
    }
  );
}

export async function apiDeleteStrings(userToken, projectId, stringSysIds) {
  if (doLog()) console.log("apiDeleteStrings()", projectId, stringSysIds.length);
  // return await makeApiRequest(
  //   `${API_SUB_PATHS.project_strings}/${projectId}/${stringId}`,
  //   "DELETE",
  //   userToken
  // );
  return await makeApiRequest(
    `${API_SUB_PATHS.project_strings}/${projectId}/delete-batch`,
    "POST",
    userToken,
    [...stringSysIds]
  );
}

export async function apiDeleteString(userToken, projectId, stringId) {
  if (doLog()) console.log("apiDeleteString() projectId stringId", projectId, stringId);
  return await makeApiRequest(
    `${API_SUB_PATHS.project_strings}/${projectId}/${stringId}`,
    "DELETE",
    userToken
  );
}

export async function apiImportStoreTextFromStore(
  userToken,
  projectId,
  storeId,
  action = "import-from-store"
) {
  if (doLog())
    console.log("apiImportStoreTextFromStore() projectId storeId", projectId, storeId);
  return await makeApiRequest(
    `${API_SUB_PATHS.project_strings}/${projectId}/import`,
    "POST",
    userToken,
    {
      action,
      storeId,
    }
  );
}

export async function apiExportProjectStrings(userToken, projectId) {
  if (doLog()) console.log("apiExportProjectStrings() projectId", projectId);
  return await makeApiRequest(
    `${API_SUB_PATHS.project_strings}/export/${projectId}`,
    "GET",
    userToken,
    null,
    null,
    { cache: { strategy: "network-only" } }
  );
}

// ======================================================================================|
//  MARK: Translations

export async function apiGenerateTranslations(
  userToken,
  projectId,
  stringType,
  force = false
) {
  if (doLog()) console.log("apiGenerateTranslations()", projectId, stringType, force);
  return await makeApiRequest(
    `${API_SUB_PATHS.project_translations}/generate`,
    "POST",
    userToken,
    { force, projectId, stringType }
  );
}

export async function apiGenerateTranslationsForString(
  userToken,
  projectId,
  stringType,
  stringId,
  force = true
) {
  if (doLog())
    console.log(
      "apiGenerateTranslationsForString()",
      projectId,
      stringType,
      stringId,
      force
    );
  return await makeApiRequest(
    `${API_SUB_PATHS.project_translations}/generate-single-string`,
    "POST",
    userToken,
    { projectId, stringType, stringId, force }
  );
}

// export async function apiPullLastTranslationLog(userToken, projectId, stringType) {
//   if (doLog()) console.log("apiPullLastTranslationLog() projectId stringType", projectId, stringType);
//   return await makeApiRequest(
//     `${API_SUB_PATHS.project_translations}/${projectId}/${stringType}/last`,
//     "GET",
//     userToken
//   );
// }

export async function apiPullTranslations(userToken, projectId, stringType, locale) {
  if (doLog()) console.log("apiPullTranslations()");
  return await makeApiRequest(
    `${API_SUB_PATHS.project_translations}/${projectId}/${stringType}`,
    "GET",
    userToken,
    null,
    {
      locale,
    }
  );
}

export async function apiPullTranslationsForString(
  userToken,
  projectId,
  stringSysId,
  storeId
) {
  if (doLog())
    console.log(
      "apiPullTranslationsForString() projectId stringSysId storeId",
      projectId,
      stringSysId,
      storeId
    );
  return await makeApiRequest(
    `${API_SUB_PATHS.project_translations}/string/${projectId}/${stringSysId}/${storeId}`,
    "GET",
    userToken
  );
}

// ======================================================================================|
//  MARK: Screens

export async function apiPullScreens(userToken, projectId, localeId, storeId) {
  if (doLog())
    console.log(
      "apiPullScreens() projectId localeId storeId",
      projectId,
      localeId,
      storeId
    );
  return await makeApiRequest(
    `${API_SUB_PATHS.project_screens}/${projectId}/${localeId}/${storeId}`,
    "GET",
    userToken
  );
}

export async function apiPublishScreens(
  userToken,
  projectId,
  storeId,
  publishType,
  localeId
) {
  if (doLog())
    console.log(
      "apiPublishScreens() projectId storeId publishType localeId",
      projectId,
      storeId,
      publishType,
      localeId
    );
  return await makeApiRequest(
    `${API_SUB_PATHS.project_screens}/publish/${projectId}`,
    "POST",
    userToken,
    {
      storeId,
      publishType,
      localeId,
    }
  );
}

// ======================================================================================|
//  MARK: Store Creds

/**
 * A7Y-1703 / A7Y-1888 — Start a keyless Google Play link.
 *
 * Returns the shared Apptonomy service account email so the UI can render the
 * "invite this account" step. The customer invites `tenantSaEmail` into Play
 * Console and grants roles; no JSON key is ever downloaded.
 *
 * `orgKey`/`tenantSaAccountId`/`tenantSaProject` remain in the response shape
 * but are informational only — the impersonated SA is now a single fixed
 * account, so there is no per-tenant SA to map or tear down.
 */
export async function apiStartKeylessPlayLink(
  userToken,
  projectId
): Promise<{
  success: boolean;
  statusCode?: number;
  data?: {
    success: boolean;
    tenantSaEmail?: string;
    tenantSaAccountId?: string;
    tenantSaProject?: string;
    orgKey?: string;
    message?: string;
  };
  error?: { message?: string };
}> {
  if (doLog()) console.log("apiStartKeylessPlayLink() projectId", projectId);
  return await makeApiRequest(
    `${API_SUB_PATHS.project_store_link}/keyless-start`,
    "POST",
    userToken,
    { projectId }
  );
}

export async function apiAddStoreLinkToProject(
  userToken,
  projectId,
  credential,
  tempCredentialId?: string
) {
  if (doLog())
    console.log(
      "apiAddStoreLinkToProject() projectId credential tempCredentialId",
      projectId,
      credential,
      tempCredentialId
    );
  return await makeApiRequest(`${API_SUB_PATHS.project_store_link}`, "POST", userToken, {
    credential,
    projectId,
    tempCredentialId,
  });
}

/**
 * Validates a store credential against the upstream store API and returns
 * the resulting permission checks plus a temporary credential reference the
 * caller can exchange when it's time to link.
 *
 * The `body` is passed through unchanged. Expected fields by store:
 *   - APPSTORE:  `{ keyId, issuerId, encryptedValue, storeId: "appstore", appId }`
 *   - PLAYSTORE (BYO): `{ encryptedValue, storeId: "playstore", bundleId, bucketName }`
 *     (A7Y-452) `bucketName` is the normalized Play Console reporting bucket
 *     name (`pubsite_prod_<id>` or `pubsite_prod_rev_<id>`); use
 *     `normalizeGpcBucketUri` from `@a7y/types` to derive it from user input.
 *   - PLAYSTORE (keyless, A7Y-1703): `{ storeId: "playstore", bundleId, bucketName?,
 *     authMode: "impersonated_sa", saEmail, orgKey?, tenantSaAccountId?,
 *     tenantSaProject? }` — NO `encryptedValue` (no secret). `saEmail` comes
 *     from `apiStartKeylessPlayLink`.
 */
/**
 * Validate a store credential before linking it to `projectId`.
 * A7Y-5057: the API authorizes `manage_credentials` on `projectId` before it
 * accepts, decrypts, probes or caches anything, so the project is required.
 */
export async function apiValidateCredential(userToken, projectId: string, credential: Record<string, unknown>) {
  if (doLog()) console.log("apiValidateCredential() projectId credential", projectId, credential);
  return await makeApiRequest(
    `${API_SUB_PATHS.project_store_link}/validate`,
    "POST",
    userToken,
    { projectId, ...credential }
  );
}

// ======================================================================================|
//  MARK: StoreLinks

export async function apiStoreUnlink(userToken, projectId, storeId) {
  if (doLog()) console.log("apiStoreLinkAction() projectId storeId", projectId, storeId);

  return await makeApiRequest(
    `${API_SUB_PATHS.project_store_link}/${projectId}/${storeId}`,
    "DELETE",
    userToken
  );
}

/**
 * Updates the Apple Sales & Trends vendor number on a project's ASC store link.
 * Hits `PATCH /:productId/project/store-link/vendor-number`. The API probes the
 * vendor/credential pair against Apple's `/v1/salesReports` and returns a
 * structured error on failure (category "format" for client-side shape issues,
 * "sales_vendor" for Apple rejecting the pairing).
 *
 * See `normalizeAscVendorNumber` in `@a7y/types` for the canonical validator.
 */
export async function apiUpdateProjectAscVendorNumber(
  userToken,
  { projectId, vendor_number }: { projectId: string; vendor_number: string }
) {
  if (doLog())
    console.log(
      "apiUpdateProjectAscVendorNumber() projectId vendor_number",
      projectId,
      vendor_number
    );
  return await makeApiRequest(
    `${API_SUB_PATHS.project_store_link}/vendor-number`,
    "PATCH",
    userToken,
    { projectId, vendor_number }
  );
}

/**
 * Updates the GPC bucket URI on a project's GPC store link.
 * Hits `PATCH /:productId/project/store-link/bucket-uri`. The API probes the
 * bucket/credential pair against GCS and returns a structured error on failure
 * (category "format" for client-side shape issues, "analytics_read" for GCS
 * rejecting the pairing). Pass an empty string to remove the bucket URI.
 *
 * See `normalizeGpcBucketUri` in `@a7y/types` for the canonical validator.
 */
export async function apiUpdateProjectGpcBucketUri(
  userToken,
  { projectId, bucket_uri }: { projectId: string; bucket_uri: string }
) {
  if (doLog())
    console.log(
      "apiUpdateProjectGpcBucketUri() projectId bucket_uri",
      projectId,
      bucket_uri
    );
  return await makeApiRequest(
    `${API_SUB_PATHS.project_store_link}/bucket-uri`,
    "PATCH",
    userToken,
    { projectId, bucket_uri }
  );
}

/**
 * Re-runs the full connect-time capability probe for a project's already-linked
 * store credential and re-persists the `capability_checks` snapshot (A7Y-2052).
 * Hits `POST /:productId/project/store-link/recheck`. Returns
 * `{ success: true, checks, persisted }` with the refreshed per-capability map
 * on success, or a structured `{ success: false, message }` on failure.
 * Button-gated in the UI because the ASC App Analytics probe POSTs an ONGOING
 * report request.
 *
 * `persisted` (A7Y-3851): `false` when the probe ran on non-production
 * compute — staging, sandbox and local dev all resolve the same production
 * Firestore, so only production compute is allowed to write the refreshed
 * snapshot back onto the project document. `checks` still reflects the fresh
 * probe result either way; only the write is skipped.
 */
export async function apiRecheckProjectStoreCapabilities(
  userToken,
  { projectId, storeId }: { projectId: string; storeId: string }
) {
  if (doLog())
    console.log(
      "apiRecheckProjectStoreCapabilities() projectId storeId",
      projectId,
      storeId
    );
  return await makeApiRequest(
    `${API_SUB_PATHS.project_store_link}/recheck`,
    "POST",
    userToken,
    { projectId, storeId }
  );
}

/**
 * A7Y-3637 — links `projectId` to the store credentials already connected on
 * `sourceProjectId`, so a second project in the same org does not have to go
 * back to the App Store Connect / Play Console to reconnect.
 * Hits `POST /:productId/project/store-link/copy-from`.
 *
 * The server re-validates against the target app before writing, so a source
 * whose credential cannot see the target app is refused rather than linked.
 * Non-2xx responses come back as `{ success: false, statusCode, error }` — the
 * body carries a `code` (`not_eligible`, `already_linked`, `credential_not_found`,
 * `no_target_app`, `no_access_to_target_app`, `listing_unavailable`) and a
 * renderable `message`.
 */
export async function apiCopyStoreLinkFromProject(
  userToken,
  {
    projectId,
    sourceProjectId,
    storeId,
  }: { projectId: string; sourceProjectId: string; storeId: string }
) {
  if (doLog())
    console.log(
      "apiCopyStoreLinkFromProject() projectId sourceProjectId storeId",
      projectId,
      sourceProjectId,
      storeId
    );
  return await makeApiRequest(
    `${API_SUB_PATHS.project_store_link}/copy-from`,
    "POST",
    userToken,
    { projectId, sourceProjectId, storeId }
  );
}

// ----------------------------------------------------------------------------
// Admin: ASC vendor number (root-level admin routes, not product-scoped).
// ----------------------------------------------------------------------------

/**
 * Admin: fetch the ASC store link for a given project.
 * Hits `GET /admin/projects/:projectId/store-link/asc`.
 */
export async function apiAdminGetProjectAscStoreLink(userToken, projectId: string) {
  if (doLog())
    console.log("apiAdminGetProjectAscStoreLink() projectId", projectId);
  return await makeNonScopedApiRequest(
    `admin/projects/${projectId}/store-link/asc`,
    "GET",
    userToken
  );
}

/**
 * Admin: set/correct the vendor number on a project's ASC store link.
 * Hits `PATCH /admin/projects/:projectId/store-link/vendor-number`.
 */
export async function apiAdminUpdateProjectAscVendorNumber(
  userToken,
  { projectId, vendor_number }: { projectId: string; vendor_number: string }
) {
  if (doLog())
    console.log(
      "apiAdminUpdateProjectAscVendorNumber() projectId vendor_number",
      projectId,
      vendor_number
    );
  return await makeNonScopedApiRequest(
    `admin/projects/${projectId}/store-link/vendor-number`,
    "PATCH",
    userToken,
    { vendor_number }
  );
}

// ======================================================================================|
//  MARK: App Text

export async function apiIngestFile(userToken, projectId, fileType, fileData) {
  if (doLog()) console.log("apiIngestFile() projectId fileType", projectId, fileType);
  return await makeApiRequest(
    `${API_SUB_PATHS.project_strings}/file/${projectId}`,
    "POST",
    userToken,
    {
      fileType,
      fileData,
      stringType: StringType.apptext,
    }
  );
}

// ======================================================================================|
//  MARK: Activity

// PATH: /project/activity/engine-state/:projectId
// Get the state of the engines for a project
export async function apiPullEngineState(userToken, projectId) {
  // if (doLog()) console.log("apiPullEngineState() projectId", projectId);
  return await makeApiRequest(
    `${API_SUB_PATHS.project_activity}/engine-state/${projectId}`,
    "GET",
    userToken
  );
}

// GET Activity List
// PATH: project/activity/list/:projectId/:stringType
export async function apiPullActivity(userToken, projectId, stringType, lastItemId) {
  // if (doLog()) console.log(
  //   "apiPullActivity() projectId stringType lastItemId",
  //   projectId,
  //   stringType,
  //   lastItemId
  // );

  return await makeApiRequest(
    `${API_SUB_PATHS.project_activity}/list/${projectId}/${stringType}`,
    "GET",
    userToken,
    null,
    { lastItemId }
  );
}

// GET Activity Item
// PATH: /item/:projectId/:itemId
export async function apiGetActivityItem(userToken, projectId, itemId) {
  // if (doLog()) console.log("apiGetActivityItem() projectId itemId", projectId, itemId);
  return await makeApiRequest(
    `${API_SUB_PATHS.project_activity}/item/${projectId}/${itemId}`,
    "GET",
    userToken
  );
}

// GET Activity Item Sublogs
// PATH: /item/sublogs/:projectId/:itemId
export async function apiGetActivityItemSublogs(userToken, projectId, itemId) {
  if (doLog())
    console.log("apiGetActivityItemSublogs() projectId itemId", projectId, itemId);
  return await makeApiRequest(
    `${API_SUB_PATHS.project_activity}/item/sublogs/${projectId}/${itemId}`,
    "GET",
    userToken
  );
}

// DELETE Activity Item
// PATH: /item/:projectId/:itemId
export async function apiDeleteActivityItem(userToken, projectId, itemId) {
  if (doLog()) console.log("apiDeleteActivityItem() projectId itemId", projectId, itemId);
  return await makeApiRequest(
    `${API_SUB_PATHS.project_activity}/item/${projectId}/${itemId}`,
    "DELETE",
    userToken
  );
}

// ======================================================================================|
//  MARK: Support

export async function apiPostSupportRequest(userToken, requestData) {
  if (doLog()) console.log("apiPostSupportRequest() requestData", requestData);
  return await makeApiRequest(
    `${API_ROOT_PATHS.support}/request`,
    "POST",
    userToken,
    requestData
  );
}

// ======================================================================================|
//  MARK: Store Presence

export async function apiListStoreLocalizations(
  userToken,
  projectId,
  storeId,
  listingType = "live"
) {
  if (doLog())
    console.log(
      "apiListStoreLocalizations() projectId storeId listingType",
      projectId,
      storeId,
      listingType
    );
  return await makeApiRequest(
    `${API_SUB_PATHS.project_presence}/${projectId}/${storeId}/listing-localizations`,
    "GET",
    userToken,
    null,
    {
      listingType,
    },
    {
      cache: {
        strategy: "network-only",
      },
    }
  );
}

// A7Y-4682: every locale's own live value, not a shared primary-locale value.
export interface LiveLocalization {
  locale: string;
  title: string;
  subtitle: string;
  keywords_field: string;
}

export async function apiGetLiveLocalizations(userToken, projectId) {
  if (doLog()) console.log("apiGetLiveLocalizations() projectId", projectId);
  return await makeApiRequest(
    `${API_SUB_PATHS.project_presence}/${projectId}/appstore/live-localizations`,
    "GET",
    userToken,
    null,
    null,
    {
      cache: {
        strategy: "network-only",
      },
    }
  );
}

// NB: only allowed for ASC on editable build
export async function apiDeleteStoreLocalization(
  userToken,
  projectId,
  storeId,
  localeId,
  localizationId
) {
  if (doLog())
    console.log(
      "apiDeleteStoreLocalization() projectId storeId localeId localizationId",
      projectId,
      storeId,
      localeId,
      localizationId
    );
  return await makeApiRequest(
    `${API_SUB_PATHS.project_presence}/${projectId}/${storeId}/listing-localizations`,
    "DELETE",
    userToken,
    null,
    {
      localeId,
      localizationId,
    }
  );
}

// NB: only allowed for ASC on editable build
export async function apiBatchDeleteStoreLocalizations(
  userToken,
  projectId,
  storeId,
  localizationObjs // array of { localeId, localizationId }
) {
  if (doLog())
    console.log(
      "apiBatchDeleteStoreLocalizations() projectId storeId",
      projectId,
      storeId
    );
  return await makeApiRequest(
    `${API_SUB_PATHS.project_presence}/${projectId}/${storeId}/listing-localizations/bulk-delete`,
    "POST",
    userToken,
    localizationObjs
  );
}

// ======================================================================================|
//  MARK: Figma

export async function apiGetFigmaAuthUrl(userToken) {
  if (doLog()) console.log("apiGetFigmaAuthUrl()");
  return await makeApiRequest(`${API_ROOT_PATHS.org}/figma/auth-url`, "GET", userToken);
}

// ======================================================================================|
//  MARK: Money

export async function apiValidatePromoCode(userToken, promoCode, projectId) {
  if (doLog()) console.log("apiValidatePromoCode() promoCode", promoCode);
  return await makeApiRequest(
    `${API_ROOT_PATHS.payment}/promo-code-validate`,
    "POST",
    userToken,
    {
      promoCode,
      projectId,
    }
  );
}

export async function apiStartCheckout(userToken, cart) {
  if (doLog()) console.log("apiStartCheckout() cart", JSON.stringify(cart));
  return await makeApiRequest(`${API_ROOT_PATHS.payment}/start`, "POST", userToken, cart);
}

export async function apiCompleteCheckout(userToken, checkoutSessionId, cancelled) {
  if (doLog()) console.log("apiCompleteCheckout()", checkoutSessionId, cancelled);
  return await makeApiRequest(`${API_ROOT_PATHS.payment}/complete`, "POST", userToken, {
    checkoutSessionId,
    cancelled,
  });
}

// ======================================================================================|
//  MARK: User

export async function apiGetUserPreferences(userToken) {
  if (doLog()) console.log("apiGetUserPreferences()");
  return await makeApiRequest(
    `${API_ROOT_PATHS.user}/preferences`,
    "GET",
    userToken
  );
}

export async function apiUpdateUserPreferences(userToken, preferences: { themeMode?: string; welcomeSeen?: boolean; upgradeBannerDismissed?: boolean; promptDismissals?: Record<string, { dismissedAt: string; behavior: "permanent" | "snoozed"; snoozeUntil?: string }>; competitorEmailMode?: "digest" | "realtime"; mutedCompetitorIds?: string[]; mutedFields?: string[]; teamReportEmails?: boolean; firstRun?: Partial<Omit<FirstRunState, "firstProjectCreatedAt">> }) {
  if (doLog()) console.log("apiUpdateUserPreferences() preferences", preferences);
  return await makeApiRequest(
    `${API_ROOT_PATHS.user}/preferences`,
    "PATCH",
    userToken,
    preferences
  );
}

export async function apiUpdateUserSignupContext(userToken, signupContext) {
  if (doLog()) console.log("apiUpdateUserSignupContext() signupContext", signupContext);
  return await makeApiRequest(
    `${API_ROOT_PATHS.user}/signup-context`,
    "PATCH",
    userToken,
    signupContext
  );
}

/**
 * A7Y-1895: post-auth affiliate lead-promotion bootstrap. Called once right
 * after signup with the referral id the client captured pre-signup, so the
 * anonymous referral is promoted visitor → lead even if the Clerk webhook's
 * unsafe_metadata path missed it. Idempotent server-side.
 */
export async function apiPromoteAffiliateLead(userToken, referralId: string) {
  if (doLog()) console.log("apiPromoteAffiliateLead() referralId", referralId);
  return await makeApiRequest(
    `${API_ROOT_PATHS.affiliate}/track/lead`,
    "POST",
    userToken,
    { referralId }
  );
}

export async function apiMergeTempAccount(userToken, tempAccountId) {
  if (doLog()) console.log("apiMergeTempAccount() tempAccountId", tempAccountId);
  return await makeApiRequest(
    `${API_ROOT_PATHS.user}/merge-temp-account`,
    "POST",
    userToken,
    { tempAccountId }
  );
}

export async function apiValidateInviteCode(userToken, inviteCode) {
  if (doLog()) console.log("apiValidateInviteCode() inviteCode", inviteCode);
  return await makeApiRequest(
    `${API_ROOT_PATHS.user}/invite-code/validate`,
    "POST",
    userToken,
    { inviteCode }
  );
}

// ======================================================================================|
//  MARK: Activity Meter

export async function apiPostActivityMeter(userToken, data) {
  if (doLog()) console.log("apiPostActivityMeter()");
  return await makeApiRequest(
    `${API_ROOT_PATHS.user}/activity-meter`,
    "POST",
    userToken,
    data
  );
}

// ======================================================================================|
//  MARK: Client Error Reporter
//  Public endpoint — userToken may be null/undefined. The route is allow-listed
//  in preflight so anonymous marketing-site visitors can ship browser errors to BQ.

export async function apiPostClientError(userToken, data) {
  if (doLog()) console.log("apiPostClientError()");
  return await makeApiRequest(
    `${API_ROOT_PATHS.user}/client-error`,
    "POST",
    userToken,
    data,
    null,            // queryParams
    undefined,       // cacheOptions
    null,            // silentStatusCodes
    false,           // requiresAuth — public route (A7Y-1311)
  );
}

// ======================================================================================|
//  MARK: Endpoint

function getApiUrlDev() {
  if (BASE_URL_OVERRIDE) return BASE_URL_OVERRIDE;
  if (typeof window !== "undefined" && window.location?.hostname) {
    return `http://${window.location.hostname}:${DEV_PORT}`;
  }
  return `http://localhost:${DEV_PORT}`;
}

/**
 * Resolves the deployed API origin for a "prod-mode" build (FORCE_PROD or
 * IS_PROD). Staging and Sandbox hosts resolve to their own fixed origin via
 * the shared contract; every other hostname (unknown/local/preview) falls
 * back to the configurable `API_PROD_ENDPOINT` — the same fallback the old
 * `-staging.` substring test used for anything that wasn't literally staging.
 */
function resolveHostedApiOrigin(): string {
  if (HOSTED_ENVIRONMENT && HOSTED_ENVIRONMENT !== "production") {
    return apiOrigin(HOSTED_ENVIRONMENT);
  }
  return API_PROD_ENDPOINT;
}

export function buildApiUrl(endpoint, queryParams) {
  let apiUrlBase =
    BASE_URL_OVERRIDE ??
    (FORCE_PROD || IS_PROD ? resolveHostedApiOrigin() : getApiUrlDev());
  apiUrlBase = `${apiUrlBase}/${PRODUCT_ID}/${endpoint}`;
  const url = new URL(apiUrlBase);
  if (queryParams) {
    Object.keys(queryParams).forEach((key) => {
      const value = queryParams[key];
      if (value !== null && value !== undefined) {
        url.searchParams.append(key, value);
      }
    });
  }

  return url.toString();
}

/**
 * Opens a server-sent-events stream WITHOUT credentials (A7Y-5678).
 *
 * Every stream this client opens authenticates by its `?streamToken=` query
 * parameter alone (`authorizeStreamRequest` / `authorizeSelfStreamRequest` in
 * a7y-api) or, for the public App Check stream, by its unguessable instance id;
 * no stream route reads a cookie. `withCredentials: true` made the browser send
 * every `.apptonomy.ai` cookie to the API host anyway, and on 2026-10-20 those
 * cookies pushed Cloudflare's OWASP Core Ruleset over its anomaly threshold
 * (rule 949110): the edge answered 403 with no CORS header, so admin unread
 * updates, messaging and project activity never connected for the affected
 * signed-in browsers while the same request without cookies reached the Worker.
 * Construct every stream through this helper so none sends cookies again.
 */
export function openStreamEventSource(url: string): EventSource {
  return new EventSource(url);
}

/**
 * Builds an API URL **without** the product-ID prefix. Used for routes mounted
 * at root level (e.g. `/messaging/*`) rather than under `/:productId/*`.
 */
export function buildNonScopedApiUrl(
  endpoint: string,
  queryParams?: Record<string, string | null | undefined> | null
): string {
  let apiUrlBase =
    BASE_URL_OVERRIDE ??
    (FORCE_PROD || IS_PROD ? resolveHostedApiOrigin() : getApiUrlDev());
  apiUrlBase = `${apiUrlBase}/${endpoint}`;
  const url = new URL(apiUrlBase);
  if (queryParams) {
    Object.keys(queryParams).forEach((key) => {
      const value = queryParams[key];
      if (value !== null && value !== undefined) {
        url.searchParams.append(key, value);
      }
    });
  }
  return url.toString();
}

/**
 * Like makeApiRequest but for non-product-scoped routes (uses buildNonScopedApiUrl).
 * Messaging endpoints live at `/messaging/*` — not under `/:productId/messaging/*`.
 */
async function makeNonScopedApiRequest(
  endpoint: string,
  method: string,
  token: string | null,
  body: unknown = null,
  queryParams: Record<string, string | null | undefined> | null = null,
  /**
   * A7Y-1311: mirrors the `requiresAuth` parameter on `makeApiRequest`. When
   * true (default) and `token` is null/undefined, short-circuit without
   * hitting the network and return a sentinel `ApiNotReady` failure.
   */
  requiresAuth = true,
) {
  if (requiresAuth && !token) {
    if (doLog())
      console.log(
        "makeNonScopedApiRequest() short-circuit: no token, requiresAuth=true",
        endpoint,
      );
    return makeNotReadyResult();
  }

  const url = buildNonScopedApiUrl(endpoint, queryParams);
  const fetchImpl = FETCH_FN ?? (typeof fetch !== "undefined" ? fetch : undefined);
  if (!fetchImpl) {
    throw new Error("fetch is not available in this environment");
  }

  // A7Y-5479: a Blob body is sent as-is under its own MIME type (the feedback
  // voice-note upload); every other body is JSON, as before.
  const isBinary = typeof Blob !== "undefined" && body instanceof Blob;
  const headers: Record<string, string> = {
    "Content-Type": isBinary ? (body as Blob).type || "application/octet-stream" : "application/json",
  };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const init: RequestInit = { method, headers };
  if (body !== null && body !== undefined) {
    init.body = isBinary ? (body as Blob) : JSON.stringify(body);
  }

  try {
    const response = await fetchImpl(url, init);
    const contentType = response.headers.get("content-type") || "";
    const isJson = contentType.includes("application/json");

    if (!response.ok) {
      const errorBody = isJson ? await response.json().catch(() => null) : await response.text().catch(() => null);
      return {
        success: false,
        statusCode: response.status,
        statusText: response.statusText,
        error: errorBody,
      };
    }

    if (response.status === 204) {
      return { success: true, statusCode: 204, data: undefined };
    }

    const data = isJson ? await response.json() : await response.text();
    return { success: true, statusCode: response.status, data };
  } catch (err: any) {
    return {
      success: false,
      statusCode: 0,
      statusText: "Network error",
      error: err?.message || err,
    };
  }
}

// ======================================================================================|
//  MARK: Request


/**
 * A7Y-4230: a success response with no body is not a JSON parse failure.
 *
 * `DELETE /extension/tokens/:id` and `DELETE /extension/token` answer
 * `204 No Content` with an empty body (`extension_route.ts:227,261`), and an
 * unconditional `res.json()` throws `Unexpected end of JSON input` there. The
 * surrounding catch then reports it as `statusCode: 0` "Network Error", so a
 * revoke that fully succeeded server-side surfaced in the UI as "Couldn't
 * revoke this extension" (measured on Sandbox, 2026-09-21). Both callers
 * already declare `ApiResult<undefined>`, so `undefined` is the contract.
 */
async function parseResponseBody(res: Response): Promise<unknown> {
  if (res.status === 204 || res.status === 205) return undefined;
  const text = await res.text();
  if (text.length === 0) return undefined;
  return JSON.parse(text);
}

async function makeApiRequest(
  endpoint: string,
  method: string,
  token: string | null | undefined,
  body: unknown = null,
  queryParams: Record<string, string | null | undefined> | null = null,
  cacheOptions: { cache?: CachePolicy } | undefined = undefined,
  /**
   * Status codes that are expected ("data not yet available", "resource
   * doesn't exist yet") and should NOT log `apiRequest() error` to the
   * console. The error response is still returned to the caller — only the
   * console noise is suppressed. The browser's own `GET ... <code>` network
   * log can't be suppressed from JS.
   */
  silentStatusCodes: ReadonlySet<number> | null = null,
  /**
   * A7Y-1311: when true (default) and `token` is null/undefined, short-circuit
   * without making a network call and return a sentinel `ApiNotReady` failure.
   * Public-by-design routes (invitation lookup, public-audit-meta, client error
   * reporter, etc.) pass `false` so they still hit the server with no token.
   */
  requiresAuth = true,
) {
  if (requiresAuth && !token) {
    if (doLog())
      console.log("makeApiRequest() short-circuit: no token, requiresAuth=true", endpoint);
    return makeNotReadyResult();
  }

  const url = buildApiUrl(endpoint, queryParams);

  // Caching only applies to GET
  const scope = decodeJwtUserScope(token);
  const cacheKey = buildCacheKey(scope, url);
  const policyDefault = getCachePolicy(endpoint, method);
  const policyOverride: CachePolicy = cacheOptions?.cache || {};
  const strategy = policyOverride.strategy || policyDefault.strategy; // 'swr' | 'network-only'
  const memoryTtlMs = policyOverride.memoryTtlMs ?? policyDefault.memoryTtlMs;
  const persistMs = policyOverride.persistMs ?? policyDefault.persistMs;
  const bypass = strategy === "network-only" || strategy === "bypass";

  if (method === "GET" && !bypass) {
    // Try memory then IDB
    const cached = await readFromCaches(cacheKey);
    const entry = cached.entry;
    const fresh = isFresh(entry);
    const withinPersist = isWithinPersist(entry);

    // If fresh, return immediately
    if (fresh) {
      return {
        success: true,
        statusCode: 200,
        data: entry.data,
      };
    }

    // If we have something within persist window, serve it and revalidate in background
    if (entry && withinPersist) {
      // background revalidate with in-flight de-dupe
      if (!__apiInFlight.has(cacheKey)) {
        const p = (async () => {
          try {
            const res = await performNetworkFetch(url, method, token, body, {
              etag: entry.etag,
              lastModified: entry.lastModified,
            });
            if (res.status === 304) {
              // Not modified; just refresh timestamps
              rememberInMemory(
                cacheKey,
                entry.data,
                memoryTtlMs,
                persistMs,
                entry.etag,
                entry.lastModified
              );
              await persistInIdb(
                cacheKey,
                entry.data,
                memoryTtlMs,
                persistMs,
                entry.etag,
                entry.lastModified
              );
            } else if (res.ok) {
              const data = await parseResponseBody(res);
              const etag = res.headers.get("ETag") || undefined;
              const lastModified = res.headers.get("Last-Modified") || undefined;
              rememberInMemory(
                cacheKey,
                data,
                memoryTtlMs,
                persistMs,
                etag,
                lastModified
              );
              await persistInIdb(
                cacheKey,
                data,
                memoryTtlMs,
                persistMs,
                etag,
                lastModified
              );
            }
          } catch {
            // ignore background errors
          } finally {
            __apiInFlight.delete(cacheKey);
          }
        })();
        __apiInFlight.set(cacheKey, p);
      }

      return {
        success: true,
        statusCode: 200,
        data: entry.data,
      };
    }

    // No cache or expired: do network with de-dupe
    if (__apiInFlight.has(cacheKey)) {
      await __apiInFlight.get(cacheKey);
      const after = await readFromCaches(cacheKey);
      if (after.entry) {
        return {
          success: true,
          statusCode: 200,
          data: after.entry.data,
        };
      }
      // fallthrough to do a direct fetch if nothing cached
    }

    const inflight = (async () => {
      try {
        const res = await performNetworkFetch(url, method, token, body, null);
        if (!res.ok) {
          const errorBody = await res.json();
          const errorObj = {
            success: false,
            statusText: res.statusText,
            statusCode: res.status,
            error: errorBody,
          };
          if (errorBody?.message === "token_expired") {
            console.warn("apiRequest() Token expired");
          } else if (!silentStatusCodes?.has(res.status)) {
            console.error("apiRequest() error", errorObj);
          }
          // A7Y-1432: hard auth-error reasons (phantom_user) fire the
          // configured onAuthError handler so the app can clear local state
          // and redirect. Recoverable reasons (token_expired) do NOT fire.
          maybeFireAuthError(res.status, errorBody);
          return errorObj;
        }

        const data = await parseResponseBody(res);
        const etag = res.headers.get("ETag") || undefined;
        const lastModified = res.headers.get("Last-Modified") || undefined;
        rememberInMemory(cacheKey, data, memoryTtlMs, persistMs, etag, lastModified);
        await persistInIdb(cacheKey, data, memoryTtlMs, persistMs, etag, lastModified);
        return {
          success: true,
          statusCode: res.status,
          data,
        };
      } catch (err) {
        return {
          success: false,
          statusText: "Network Error",
          statusCode: 0,
          error: { message: err?.message || "network_error" },
        };
      } finally {
        __apiInFlight.delete(cacheKey);
      }
    })();
    __apiInFlight.set(cacheKey, inflight);
    const result = await inflight;
    return result;
  }

  // Non-GET or bypass: direct network, then invalidate if mutation
  try {
    const res = await performNetworkFetch(url, method, token, body, null);
    if (!res.ok) {
      const errorBody = await res.json();
      const errorObj = {
        success: false,
        statusText: res.statusText,
        statusCode: res.status,
        error: errorBody,
      };
      if (errorBody?.message === "token_expired") {
        console.warn("apiRequest() Token expired");
      } else if (!silentStatusCodes?.has(res.status)) {
        // A7Y-2554: `silentStatusCodes` has to be honoured here too, not just
        // on the GET branch — the endpoints that pass it (public-meta) are
        // POSTs, so the GET-only check left them logging every expected 404.
        console.error("apiRequest() error", errorObj);
      }
      // A7Y-1432: hard auth-error reasons (phantom_user) fire the configured
      // onAuthError handler. See the GET path above for full rationale.
      maybeFireAuthError(res.status, errorBody);
      return errorObj;
    }

    const data = await parseResponseBody(res);

    // For GET requests with network-only strategy, update the cache so subsequent
    // SWR reads see fresh data instead of returning stale entries.
    if (method === "GET" && strategy === "network-only") {
      const etag = res.headers.get("ETag") || undefined;
      const lastModified = res.headers.get("Last-Modified") || undefined;
      rememberInMemory(cacheKey, data, memoryTtlMs, persistMs, etag, lastModified);
      persistInIdb(cacheKey, data, memoryTtlMs, persistMs, etag, lastModified).catch(() => {});
    }

    // Invalidate related caches on mutations
    if (method !== "GET") {
      invalidateCachesForMutation(scope, endpoint, method, body).catch(() => {});
    }

    return {
      success: true,
      statusCode: res.status,
      data,
    };
  } catch (err) {
    return {
      success: false,
      statusText: "Network Error",
      statusCode: 0,
      error: { message: err?.message || "network_error" },
    };
  }
}

// ======================================================================================|
// MARK: Response Unwrap Helper

/**
 * Safely extract the payload from a `makeApiRequest` result.
 *
 * API endpoints return `{ data: <payload> }` as the JSON body.
 * `makeApiRequest` wraps that as `{ success, statusCode, data: <body> }`.
 * So the payload lives at `result.data.data`.
 *
 * This helper handles both the standard double-wrapped shape **and** the
 * edge-case where only a single layer of wrapping is present.
 *
 * Returns `null` when the result indicates failure or no payload.
 */
export function extractApiData<T = unknown>(result: unknown): T | null {
  if (!result || typeof result !== "object") return null;
  const r = result as Record<string, unknown>;
  if (!r.success) return null;
  if (!("data" in r) || r.data == null) return null;

  const outer = r.data;
  // Standard path: result.data is the JSON body, e.g. { data: <payload> }
  if (typeof outer === "object" && outer !== null && "data" in outer) {
    const inner = (outer as Record<string, unknown>).data;
    // If the inner data is non-null, use it as the payload.
    // If inner IS null, the API explicitly returned { data: null } — propagate
    // that as null rather than falling through to return the wrapper object.
    return inner != null ? (inner as T) : null;
  }

  // Fallback: result.data IS the payload directly (no extra wrapper)
  return outer as T;
}

// ======================================================================================|
//  MARK: Organizations

export async function apiListUserOrgs(userToken, skipCache = false) {
  if (doLog()) console.log("apiListUserOrgs()");
  const cacheOptions = skipCache ? { cache: { strategy: "network-only" as const } } : undefined;
  return await makeApiRequest(`${API_ROOT_PATHS.org}/orgs`, "GET", userToken, null, null, cacheOptions);
}

export async function apiCreateOrg(userToken, name: string, slug?: string, timezone?: string) {
  if (doLog()) console.log("apiCreateOrg() name slug timezone", name, slug, timezone);
  return await makeApiRequest(`${API_ROOT_PATHS.org}/orgs`, "POST", userToken, { name, slug, timezone });
}

export async function apiDeleteOrg(userToken, orgId: string) {
  if (doLog()) console.log("apiDeleteOrg() orgId", orgId);
  return await makeApiRequest(`${API_ROOT_PATHS.org}/orgs/${orgId}`, "DELETE", userToken);
}

export async function apiUpdateOrg(userToken, orgId: string, updates: { name?: string; slug?: string; timezone?: string; iconUrl?: string; metadata?: any; settings?: OrgSettingsUpdate }) {
  if (doLog()) console.log("apiUpdateOrg() orgId", orgId, "updates", updates);
  return await makeApiRequest(`${API_ROOT_PATHS.org}/orgs/${orgId}`, "PATCH", userToken, updates);
}

export async function apiUploadOrgIcon(userToken, orgId: string, imageDataUrl: string) {
  if (doLog()) console.log("apiUploadOrgIcon() orgId", orgId);
  return await makeApiRequest(`${API_ROOT_PATHS.org}/orgs/${orgId}/icon`, "POST", userToken, { imageDataUrl });
}

export async function apiDeleteOrgIcon(userToken, orgId: string) {
  if (doLog()) console.log("apiDeleteOrgIcon() orgId", orgId);
  return await makeApiRequest(`${API_ROOT_PATHS.org}/orgs/${orgId}/icon`, "DELETE", userToken);
}

// ======================================================================================|
//  MARK: Org Overview Apps (A7Y-3577)

/**
 * `makeApiRequest` never returns the JSON body directly — it returns the
 * `{ success, statusCode, data | error }` envelope (cache hit and network
 * path alike). The overview-apps fetchers below used to cast that envelope to
 * their payload type, so `OverviewAppsSection` called `.filter` on an object
 * (measured 2026-09-07 on Sandbox, first login: `TypeError: o.filter is not a
 * function` at OverviewAppsSection.tsx:55). This unwraps on success and throws
 * on failure, which React Query reports as the query/mutation error state.
 */
export function unwrapApiResult<T>(result: unknown, what: string): T {
  const envelope = result as { success?: boolean; statusCode?: number; data?: T; error?: unknown } | null;
  if (envelope && envelope.success === true) return envelope.data as T;
  const status = envelope?.statusCode ?? 0;
  const detail =
    envelope && typeof envelope.error === "object" && envelope.error
      ? JSON.stringify(envelope.error).slice(0, 200)
      : String(envelope?.error ?? "no response");
  throw new Error(`${what} failed (${status}): ${detail}`);
}

export async function getOrgOverviewApps(userToken, orgId: string): Promise<OrgOverviewApp[]> {
  if (doLog()) console.log("getOrgOverviewApps() orgId", orgId);
  const data = unwrapApiResult<OrgOverviewApp[]>(
    await makeApiRequest(`${API_ROOT_PATHS.org}/orgs/${orgId}/overview-apps`, "GET", userToken),
    "getOrgOverviewApps",
  );
  return Array.isArray(data) ? data : [];
}

export async function importOrgOverviewApps(
  userToken,
  orgId: string,
  apps: Array<{ storeId: StoreId; storeAppId: string; selectedForAudit?: boolean; counterpartId?: string }>,
) {
  if (doLog()) console.log("importOrgOverviewApps() orgId apps", orgId, apps);
  return unwrapApiResult<{ imported: OrgOverviewApp[]; failed: Array<{ storeAppId: string; reason: string }> }>(
    await makeApiRequest(`${API_ROOT_PATHS.org}/orgs/${orgId}/overview-apps/import`, "POST", userToken, { apps }),
    "importOrgOverviewApps",
  );
}

export async function bulkDeleteOrgOverviewApps(userToken, orgId: string, ids: string[]) {
  if (doLog()) console.log("bulkDeleteOrgOverviewApps() orgId ids", orgId, ids);
  return unwrapApiResult<{ deleted: number }>(
    await makeApiRequest(`${API_ROOT_PATHS.org}/orgs/${orgId}/overview-apps/bulk-delete`, "POST", userToken, { ids }),
    "bulkDeleteOrgOverviewApps",
  );
}

export async function refreshOrgOverviewApps(userToken, orgId: string) {
  if (doLog()) console.log("refreshOrgOverviewApps() orgId", orgId);
  return unwrapApiResult<{ refreshed: number; skipped: number }>(
    await makeApiRequest(`${API_ROOT_PATHS.org}/orgs/${orgId}/overview-apps/refresh`, "POST", userToken),
    "refreshOrgOverviewApps",
  );
}

// A7Y-3579 — convert an overview row into a real project and join the org's
// initial-audit queue. Returns the raw makeApiRequest envelope (never throws
// on a non-2xx response) so the caller can distinguish a 402/403 entitlement
// denial or a 403 QUOTA_EXCEEDED body from a generic failure, the same way
// `apiCreateProject()` surfaces those bodies to `ProjectCreatorRoot`.
export async function convertOverviewApp(userToken, orgId: string, id: string) {
  if (doLog()) console.log("convertOverviewApp() orgId id", orgId, id);
  return await makeApiRequest(
    `${API_ROOT_PATHS.org}/orgs/${orgId}/overview-apps/convert`,
    "POST",
    userToken,
    { id },
  );
}

// ======================================================================================|
//  MARK: Publisher Portfolio (A7Y-3578)

export async function lookupPublisherPortfolio(
  userToken,
  orgId: string,
  params: { storeId: StoreId; query: string; country?: string },
) {
  if (doLog()) console.log("lookupPublisherPortfolio() orgId params", orgId, params);
  return (await makeApiRequest(
    `${API_ROOT_PATHS.org}/orgs/${orgId}/publisher-portfolio`,
    "GET",
    userToken,
    null,
    {
      storeId: params.storeId,
      query: params.query,
      ...(params.country ? { country: params.country } : {}),
    },
    { cache: { strategy: "network-only" } },
  )) as unknown as import("@a7y/types").PublisherPortfolioLookupResponse;
}

export async function importPublisherPortfolio(
  userToken,
  orgId: string,
  body: import("@a7y/types").PortfolioImportRequest,
) {
  if (doLog()) console.log("importPublisherPortfolio() orgId body", orgId, body);
  return (await makeApiRequest(
    `${API_ROOT_PATHS.org}/orgs/${orgId}/publisher-portfolio/import`,
    "POST",
    userToken,
    body,
  )) as unknown as import("@a7y/types").PortfolioImportResponse;
}

// ======================================================================================|
//  MARK: Organization Members

export async function apiListOrgMembers(userToken, orgId, skipCache = false) {
  if (doLog()) console.log("apiListOrgMembers() orgId", orgId, "skipCache", skipCache);
  const cacheOptions = skipCache ? { cache: { strategy: "network-only" as const } } : undefined;
  return await makeApiRequest(`${API_ROOT_PATHS.org}/orgs/${orgId}/members`, "GET", userToken, null, null, cacheOptions);
}

export async function apiRemoveOrgMember(userToken, orgId, userId) {
  if (doLog()) console.log("apiRemoveOrgMember() orgId userId", orgId, userId);
  return await makeApiRequest(
    `${API_ROOT_PATHS.org}/orgs/${orgId}/members/${userId}`,
    "DELETE",
    userToken
  );
}

export async function apiUpdateOrgMemberRole(userToken, orgId, userId, role) {
  if (doLog()) console.log("apiUpdateOrgMemberRole() orgId userId role", orgId, userId, role);
  return await makeApiRequest(
    `${API_ROOT_PATHS.org}/orgs/${orgId}/members/${userId}/role`,
    "PATCH",
    userToken,
    { role }
  );
}

export async function apiListOrgInvitations(userToken, orgId, skipCache = false) {
  if (doLog()) console.log("apiListOrgInvitations() orgId", orgId, "skipCache", skipCache);
  const cacheOptions = skipCache ? { cache: { strategy: "network-only" as const } } : undefined;
  return await makeApiRequest(
    `${API_ROOT_PATHS.org}/orgs/${orgId}/invitations`,
    "GET",
    userToken,
    null,
    null,
    cacheOptions
  );
}

export async function apiCreateOrgInvitation(userToken, orgId, email, role) {
  if (doLog()) console.log("apiCreateOrgInvitation() orgId email role", orgId, email, role);
  return await makeApiRequest(
    `${API_ROOT_PATHS.org}/orgs/${orgId}/invitations`,
    "POST",
    userToken,
    { email, role }
  );
}

export async function apiRevokeOrgInvitation(userToken, orgId, invitationId) {
  if (doLog()) console.log("apiRevokeOrgInvitation() orgId invitationId", orgId, invitationId);
  return await makeApiRequest(
    `${API_ROOT_PATHS.org}/orgs/${orgId}/invitations/${invitationId}`,
    "DELETE",
    userToken
  );
}

export async function apiGetInvitationByToken(token) {
  if (doLog()) console.log("apiGetInvitationByToken() token", token);
  // Public endpoint - no auth required
  return await makeApiRequest(
    `${API_ROOT_PATHS.org}/invitations/${token}`,
    "GET",
    null,
    null,            // body
    null,            // queryParams
    undefined,       // cacheOptions
    null,            // silentStatusCodes
    false,           // requiresAuth — public route (A7Y-1311)
  );
}

export async function apiAcceptInvitation(userToken, token) {
  if (doLog()) console.log("apiAcceptInvitation() token", token);
  return await makeApiRequest(
    `${API_ROOT_PATHS.org}/invitations/${token}/accept`,
    "POST",
    userToken
  );
}

export async function apiClaimPendingInvitations(userToken) {
  if (doLog()) console.log("apiClaimPendingInvitations()");
  return await makeApiRequest(
    `${API_ROOT_PATHS.org}/invitations/claim`,
    "POST",
    userToken
  );
}

export async function apiRedeemActivationToken(userToken, activationToken) {
  if (doLog()) console.log("apiRedeemActivationToken() token", activationToken);
  return await makeApiRequest(
    `${API_ROOT_PATHS.user}/activate/${activationToken}`,
    "POST",
    userToken
  );
}

// ======================================================================================|
//  MARK: Organization Integrations

export async function apiListOrgIntegrations(userToken, orgId: string) {
  if (doLog()) console.log("apiListOrgIntegrations() orgId", orgId);
  return await makeApiRequest(
    `${API_ROOT_PATHS.org}/orgs/${orgId}/integrations`,
    "GET",
    userToken
  );
}

// ======================================================================================|
//  MARK: Organization Audit Log (A7Y-4293)

function orgAuditLogQuery(filters: OrgAuditLogFilters | undefined) {
  return {
    action: filters?.actions && filters.actions.length > 0 ? filters.actions.join(",") : null,
    from: filters?.from || null,
    to: filters?.to || null,
    projectId: filters?.projectId || null,
  };
}

/**
 * One page of the organisation's audit log (owner/admin only). Pass the
 * previous page's `nextCursor` to continue. Always network-only: an audit log
 * served from a stale cache would hide the newest entries.
 */
export async function apiListOrgAuditLog(
  userToken,
  orgId: string,
  options: OrgAuditLogFilters & { limit?: number; cursor?: string | null } = {}
): Promise<ApiResult<OrgAuditLogPage>> {
  if (doLog()) console.log("apiListOrgAuditLog() orgId", orgId);
  return (await makeApiRequest(
    `${API_ROOT_PATHS.org}/orgs/${orgId}/audit-log`,
    "GET",
    userToken,
    null,
    {
      ...orgAuditLogQuery(options),
      limit: options.limit ? String(options.limit) : null,
      cursor: options.cursor || null,
    },
    { cache: { strategy: "network-only" } }
  )) as ApiResult<OrgAuditLogPage>;
}

/**
 * Export the organisation's filtered audit log as CSV or JSON. Returns the
 * response body as text (the API serves it as an attachment), ready to be
 * wrapped in a Blob by the caller.
 */
export async function apiExportOrgAuditLog(
  userToken,
  orgId: string,
  format: OrgAuditLogExportFormat,
  filters?: OrgAuditLogFilters
): Promise<ApiResult<string>> {
  if (doLog()) console.log("apiExportOrgAuditLog() orgId format", orgId, format);
  if (!userToken) return makeNotReadyResult();
  const url = buildApiUrl(`${API_ROOT_PATHS.org}/orgs/${orgId}/audit-log`, {
    ...orgAuditLogQuery(filters),
    format: format === "csv" ? "csv" : "export-json",
  });
  try {
    const res = await performNetworkFetch(url, "GET", userToken, null, null);
    if (!res.ok) {
      const error = await res.json().catch(() => null);
      maybeFireAuthError(res.status, error);
      return { success: false, statusCode: res.status, statusText: res.statusText, error };
    }
    return { success: true, statusCode: res.status, data: await res.text() };
  } catch (err) {
    return {
      success: false,
      statusCode: 0,
      statusText: "Network Error",
      error: { message: err?.message || "network_error" },
    };
  }
}

export async function apiGetOrgEngagement(userToken, orgId: string) {
  if (doLog()) console.log("apiGetOrgEngagement() orgId", orgId);
  return await makeApiRequest(
    `${API_ROOT_PATHS.org}/orgs/${orgId}/engagement`,
    "GET",
    userToken
  );
}

export async function apiGetOrgIntegration(userToken, orgId: string, provider: string) {
  if (doLog()) console.log("apiGetOrgIntegration() orgId provider", orgId, provider);
  return await makeApiRequest(
    `${API_ROOT_PATHS.org}/orgs/${orgId}/integrations/${provider}`,
    "GET",
    userToken
  );
}

export async function apiGetSlackAuthUrl(userToken, orgId: string) {
  if (doLog()) console.log("apiGetSlackAuthUrl() orgId", orgId);
  return await makeApiRequest(
    `${API_ROOT_PATHS.org}/orgs/${orgId}/integrations/slack/auth-url`,
    "GET",
    userToken
  );
}

export async function apiListSlackChannels(userToken, orgId: string) {
  if (doLog()) console.log("apiListSlackChannels() orgId", orgId);
  return await makeApiRequest(
    `${API_ROOT_PATHS.org}/orgs/${orgId}/integrations/slack/channels`,
    "GET",
    userToken
  );
}

export async function apiDisconnectOrgIntegration(userToken, orgId: string, provider: string) {
  if (doLog()) console.log("apiDisconnectOrgIntegration() orgId provider", orgId, provider);
  return await makeApiRequest(
    `${API_ROOT_PATHS.org}/orgs/${orgId}/integrations/${provider}`,
    "DELETE",
    userToken
  );
}

// ======================================================================================|
//  MARK: ASO Audit

export async function apiRunAsoAudit(
  userToken,
  auditRequest: {
    listingSource?: "draft";
    projectId?: string;
    appId?: string;
    bundleId?: string;
    storefront: "appstore" | "playstore";
    countryCode?: string;
    languageCode?: string; // Play Store hl param (language), e.g., "en", "es", "fr"
    forceRefresh?: boolean;
    skipCacheOptions?: {
      all?: boolean;
      audit?: boolean;
      keyword?: boolean;
      screenshot?: boolean;
      icon?: boolean;
      competitor?: boolean;
      sentiment?: boolean;
      storetext?: boolean;
    };
    org_id?: string;
    /**
     * A7Y-506: Optional single-locale override (wire format, e.g. "en_us").
     * When omitted the backend fans out to every locale configured for the
     * project (`primary` + `targetLocales`).
     */
    locale?: string;
  }
) {
  if (doLog())
    console.log("apiRunAsoAudit() auditRequest", auditRequest);
  return await makeApiRequest(
    "aso/listing-audit",
    "POST",
    userToken,
    auditRequest,
    null,
    { cache: { strategy: "network-only" } }
  );
}

/** @deprecated Use apiRunAsoAudit. The persisted Firestore collection is AsoAudit. */
export const apiRunAsoListingAudit = apiRunAsoAudit;

export async function apiGetAsoAudits(
  userToken,
  request: {
    appIdentifier: string;
    limit?: number;
    /**
     * Current project ID. `/list` scopes reads to this persisted `project_id`.
     */
    projectId: string;
    /**
     * A7Y-506: Optional locale filter (wire format, e.g. "en_us"). When
     * supplied, `/list` returns only audits for that locale. Omit to see the
     * full multi-locale history.
     */
    locale?: string;
  }
) {
  if (doLog())
    console.log("apiGetAsoAudits() request", request);
  // Guard: the server returns 400 when appIdentifier is missing. Short-circuit
  // here so stray refetches (e.g. during project creation before an app is
  // linked) don't generate noisy server-side error logs.
  if (!request?.appIdentifier) {
    return {
      success: false,
      statusCode: 400,
      statusText: "Bad Request",
      error: { message: "appIdentifier is required" },
    };
  }
  if (!request?.projectId) {
    return {
      success: false,
      statusCode: 400,
      statusText: "Bad Request",
      error: { message: "projectId is required" },
    };
  }
  return await makeApiRequest(
    "aso/listing-audit/list",
    "POST",
    userToken,
    request,
    null,
    { cache: { strategy: "network-only" } }
  );
}

export async function apiGetAiDiscoveryTranscript(
  userToken,
  request: {
    auditId: string;
  }
) {
  if (doLog())
    console.log("apiGetAiDiscoveryTranscript() request", request);
  return await makeApiRequest(
    "aso/listing-audit/ai-discovery-transcript",
    "POST",
    userToken,
    request,
    null,
    { cache: { strategy: "network-only" } }
  );
}

/**
 * Update the sharePermitted flag on an audit.
 */
export async function apiUpdateAuditShare(
  userToken,
  request: {
    auditId: string;
    sharePermitted: boolean;
  }
) {
  if (doLog())
    console.log("apiUpdateAuditShare() request", request);
  return await makeApiRequest(
    "aso/listing-audit/update-share",
    "POST",
    userToken,
    request,
    null,
    { cache: { strategy: "network-only" } }
  );
}

/**
 * A7Y-1166 / A7Y-1191: response shape returned by both `/get-meta`
 * (authenticated) and `/public-meta` (unauth, sharePermitted). Two states
 * a caller must handle:
 *
 *  - **GCS-backed audit (post-A7Y-1166 write path)**: `v2_meta` populated
 *    + `body_signed_url` populated. Fetch the body via
 *    {@link apiFetchSignedUrl}.
 *  - **No V2 envelope**: both `v2_meta` and `body_signed_url` are null.
 *    Caller falls back to V1 (`header`).
 *
 * The wire shape is defined by the server in
 * `a7y-api/src/routes/aso/listing-audit.ts:buildAuditMetaEnvelope`.
 */
export type AsoAuditMetaResponse = {
  success: true;
  /** V1 audit header (denormalized) for back-compat consumers. */
  header: unknown;
  /** V2Meta projection. Null when audit has no V2 envelope. */
  v2_meta: unknown | null;
  /** Firebase Storage signed URL for the gzipped V2 body. Present iff v2_meta is. */
  body_signed_url: string | null;
  /**
   * A7Y-1179: scan-claim / lead-audit / teaser-report workflow fields
   * projected from the parent AsoAudit doc. These are NOT in `v2_meta` (they
   * are workflow state, not V2 envelope fields). Keys include
   * `is_anonymous`, `claim_token`, `user_id`, `org_id`, `project_id`,
   * `full_audit_id`, `full_audit_status`, `full_audit_error`,
   * `scan_audit_status`, `lead_audit_status`, `lead_audit_error`.
   * Only present keys are populated (omitted when null/undefined).
   */
  parent_workflow: Record<string, unknown>;
};

/**
 * A7Y-1166 / A7Y-1191: Fetch the small V2 meta envelope for a single
 * audit. Returns an {@link AsoAuditMetaResponse}. The full audit body
 * lives in Firebase Storage; clients fetch it via
 * {@link apiFetchSignedUrl} using the returned `body_signed_url`. Audits
 * without a V2 envelope return `v2_meta = null` and `body_signed_url =
 * null`; callers fall back to V1 (`header`).
 *
 * BE owns the route in `a7y-api/src/routes/aso/listing-audit.ts` (W4.1 of
 * A7Y-1166); this api-client export is the FE-side contract.
 */
export async function apiGetAuditMeta(
  userToken,
  request: {
    auditId: string;
  }
) {
  if (doLog())
    console.log("apiGetAuditMeta() request", request);
  return await makeApiRequest(
    "aso/listing-audit/get-meta",
    "POST",
    userToken,
    request,
    null,
    { cache: { strategy: "network-only" } }
  );
}

/**
 * A7Y-1166: Public counterpart of {@link apiGetAuditMeta}. No auth token
 * required; returns the same envelope shape when the underlying audit has
 * `sharePermitted = true`, otherwise 403.
 */
export async function apiGetPublicAuditMeta(
  request: {
    auditId: string;
  }
) {
  if (doLog())
    console.log("apiGetPublicAuditMeta() request", request);
  return await makeApiRequest(
    "aso/listing-audit/public-meta",
    "POST",
    null, // No auth token required
    request,
    null,
    { cache: { strategy: "network-only" } },
    // A7Y-2554: the /scan result page polls this endpoint from the moment the
    // engine run is dispatched, so 404 (audit doc not written yet) and 403
    // (share flag not stamped yet) are both expected states, not errors.
    new Set([403, 404]), // silentStatusCodes
    false,           // requiresAuth — public route (A7Y-1311)
  );
}

/**
 * A7Y-1364 / A7Y-2812: response envelope for `POST aso/listing-audit/pdf`.
 *
 * Wraps the on-the-wire JSON in `makeApiRequest`'s standard
 * `{ success, statusCode, data }` envelope. On a 2xx response, `data` carries
 * one of the three variants below:
 *
 * - `'ready'`: the renderer has already produced the PDF. `signedUrl` is a
 *   short-lived V4 signed download URL the caller can hand directly to a
 *   hidden `<a download>` element. `filename` is the user-facing filename
 *   stamped by the renderer.
 * - `'rendering'`: a render generation is live — either newly enqueued by this
 *   call (`reason: 'enqueued'`) or already in flight (`reason: 'in-flight'`,
 *   which also covers a Cloud Tasks `duplicate`). Keep polling.
 * - `'failed'`: the render reached a terminal failure (`'render_failed'`) or
 *   went silent past the stall window without stamping a terminal status
 *   (`'render_stalled'`). Stop polling and offer a retry.
 *
 * The full `/pdf` status contract (A7Y-2812 §1):
 *
 * | HTTP | Body | Condition | Client action |
 * |---|---|---|---|
 * | 200 | `{ status:'ready', signedUrl, filename }` | `pdfStatus ∈ {'ready','oversized'}` | download → track → stop |
 * | 202 | `{ status:'rendering', reason:'enqueued', generation }` | `start===true` and no live render | keep polling |
 * | 202 | `{ status:'rendering', reason:'in-flight', generation, enqueuedAt }` | a render is in flight (also on enqueue `duplicate`) | keep polling |
 * | 200 | `{ status:'failed', reason:'render_failed', generation, error? }` | poll call, `pdfStatus === 'failed'` | stop, error toast + Try again |
 * | 200 | `{ status:'failed', reason:'render_stalled', generation }` | poll call, `pending`/absent and not in flight | stop, error toast + Try again |
 * | 401/403/404 | `{ success:false, error }` | authz / lookup failure | stop, error toast, no retry affordance |
 * | 500 | `{ success:false, error }` | enqueue threw; doc also stamped `failed` | stop, error toast + Try again |
 *
 * **`status:'failed'` arrives on HTTP 200, deliberately.** The *query*
 * succeeded and reported a known terminal state. `makeApiRequest` only
 * populates `data` on a 2xx response, so returning a 5xx here would push this
 * discriminated union into `ApiFailure.error` and force every client to parse
 * an untyped body — the exact error-swallowing A7Y-2812 exists to remove.
 *
 * `enqueuedAt` is an ISO 8601 string; the server never puts a raw Firestore
 * timestamp shape on the wire.
 */
export type AuditPdfData =
  | { status: "ready"; signedUrl: string; filename: string }
  | { status: "rendering"; reason: "enqueued" | "in-flight"; generation: number; enqueuedAt?: string }
  | { status: "failed"; reason: "render_failed" | "render_stalled"; generation: number; error?: string };

/**
 * A7Y-1364 / A7Y-2812: request the rendered audit PDF.
 *
 * The webapp calls this when the user clicks the PDF export button, then polls
 * it until the status resolves. See {@link AuditPdfData} for the full response
 * table.
 *
 * **`start: true` means "the user just asked for this PDF."** Only a call
 * carrying `start` may create a render generation; a bare poll never does.
 * The caller must send `start: true` on **every** call until a request
 * demonstrably **reaches the server** — that is, until it gets back a response
 * that is not the `ApiNotReady` sentinel — and drop it from then on. It is
 * **not** keyed off the loop index: `makeApiRequest` short-circuits locally
 * when `requiresAuth && !token`, so an iteration that never left the browser
 * must not consume the click's one `start`. Two `start: true` calls in one
 * click are harmless — the second lands against a now-fresh `pending` doc and
 * takes the in-flight branch.
 *
 * Network-only — no client-side caching (PDF readiness is server-state that
 * can transition without an explicit invalidation event).
 *
 * Returns the standard api-client envelope; consumers should read
 * `result.data` as {@link AuditPdfData}.
 *
 * BE owns the route in `a7y-api/src/routes/aso/listing-audit.ts`.
 */
export async function apiRequestAuditPdf(
  userToken: string | null,
  request: { auditId: string; start?: boolean }
) {
  if (doLog())
    console.log("apiRequestAuditPdf() request", request);
  return await makeApiRequest(
    "aso/listing-audit/pdf",
    "POST",
    userToken,
    request,
    null,
    { cache: { strategy: "network-only" } }
  );
}

/**
 * A7Y-1166: Fetch a Firebase Storage signed URL and decode the response as
 * JSON. Used by the audit shell to load the V2 audit body that lives in GCS.
 * Throws an Error with `{status}` attached on non-2xx responses so callers
 * can implement retry-once-then-fail semantics on 403.
 *
 * Handles both `Content-Encoding: gzip` (decompressed transparently by the
 * runtime fetch) and the GCS path where the object was uploaded with a
 * `Content-Encoding: gzip` metadata flag — in both cases the runtime exposes
 * the decoded JSON body via `res.json()`.
 */
export async function apiFetchSignedUrl<T>(signedUrl: string): Promise<T> {
  if (doLog()) console.log("apiFetchSignedUrl() request", { signedUrl: signedUrl.slice(0, 64) + "…" });
  const fetchImpl = FETCH_FN ?? (typeof fetch !== "undefined" ? fetch : undefined);
  if (!fetchImpl) throw new Error("fetch is not available in this environment");
  const res = await fetchImpl(signedUrl, { method: "GET" });
  if (!res.ok) {
    const err = new Error(
      `Signed URL fetch failed with status ${res.status}: ${res.statusText}`,
    ) as Error & { status: number };
    err.status = res.status;
    throw err;
  }
  return (await res.json()) as T;
}


// ======================================================================================|
//  MARK: ASO Competitor Discovery

export async function apiRunCompetitorDiscovery(
  userToken,
  request: {
    appIdentifier: string;
    storeId: "appstore" | "playstore";
    countryCode?: string;
    /** Language code (hl parameter) - used for Play Store only */
    languageCode?: string;
    /** Skip cache and fetch fresh data */
    skipCache?: boolean;
  }
) {
  if (doLog())
    console.log("apiRunCompetitorDiscovery() request", request);
  return await makeApiRequest(
    "aso/competitor-discovery",
    "POST",
    userToken,
    request,
    null,
    { cache: { strategy: "network-only" } }
  );
}

// ======================================================================================|
//  MARK: ASO Screenshot Analysis

export async function apiRunScreenshotAnalysis(
  userToken,
  request: {
    appIdentifier: string;
    storeId: "appstore" | "playstore";
    countryCode?: string;
    /** Language code (hl parameter) - used for Play Store only */
    languageCode?: string;
    /** AI provider to use (defaults to 'openai') */
    providerId?: "gemini" | "openai";
    /** AI model to use */
    modelId?: string;
    /** Skip cache and fetch fresh data */
    skipCache?: boolean;
  }
) {
  if (doLog())
    console.log("apiRunScreenshotAnalysis() request", request);
  return await makeApiRequest(
    "aso/screenshot-analysis",
    "POST",
    userToken,
    request,
    null,
    { cache: { strategy: "network-only" } }
  );
}

// ======================================================================================|
//  MARK: ASO Icon Analysis

export async function apiRunIconAnalysis(
  userToken,
  request: {
    appIdentifier: string;
    storeId: "appstore" | "playstore";
    countryCode?: string;
    /** Language code (hl parameter) - used for Play Store only */
    languageCode?: string;
    /** Skip cache and fetch fresh data */
    skipCache?: boolean;
  }
) {
  if (doLog()) console.log("apiRunIconAnalysis() request", request);
  return await makeApiRequest(
    "aso/icon-analysis",
    "POST",
    userToken,
    request,
    null,
    { cache: { strategy: "network-only" } }
  );
}

// ======================================================================================|
//  MARK: ASO Keyword Analysis

export async function apiRunKeywordAnalysis(
  userToken,
  request: {
    appIdentifier: string;
    storeId: "appstore" | "playstore";
    countryCode?: string;
    /** Language code (hl parameter) - used for Play Store only */
    languageCode?: string;
    /** Optional primary use case description for better relevance scoring */
    primaryUseCase?: string | null;
    /** Skip cache and fetch fresh data */
    skipCache?: boolean;
    /** Include auto-discovered competitors in keyword analysis */
    includeCompetitors?: boolean;
  }
) {
  if (doLog()) console.log("apiRunKeywordAnalysis() request", request);
  return await makeApiRequest(
    "aso/keyword-analysis",
    "POST",
    userToken,
    request,
    null,
    { cache: { strategy: "network-only" } }
  );
}

// ======================================================================================|
//  MARK: Teaser Report

export type TeaserReportRequest = {
  appIdentifier: string;
  storeId: "appstore" | "playstore";
  countryCode?: string;
};

export async function apiTriggerTeaserReport(
  userToken,
  request: TeaserReportRequest
) {
  if (doLog()) console.log("apiTriggerTeaserReport() request", request);
  return await makeApiRequest(
    "aso/teaser-report",
    "POST",
    userToken,
    request,
    null,
    { cache: { strategy: "network-only" } }
  );
}

// ======================================================================================|
//  MARK: Stream tokens (A7Y-2218)

export type StreamTokenPurpose =
  | "project-activity"
  | "publish-progress"
  | "aso-engine"
  | "messaging";

/**
 * Mints a short-lived, single-use stream token for an authenticated SSE
 * connection. The normal Clerk Bearer token authenticates this REST call (via
 * the standard Authorization header); the returned `streamToken` is what gets
 * passed to the EventSource as a `?streamToken=` query param. Tokens expire in
 * ≤120s and are single-use, so a fresh one must be minted for every connect and
 * every reconnect.
 */
export async function apiMintStreamToken(
  userToken: string | null | undefined,
  { purpose, resource }: { purpose: StreamTokenPurpose; resource: string }
): Promise<string> {
  const response = (await makeApiRequest(
    "stream-token",
    "POST",
    userToken,
    { purpose, resource },
    null,
    { cache: { strategy: "network-only" } }
  )) as { success?: boolean; statusCode?: number; data?: { streamToken?: string } };
  if (!response?.success || !response?.data?.streamToken) {
    throw new Error(
      `Failed to mint stream token (purpose: ${purpose}, status: ${response?.statusCode ?? "unknown"})`
    );
  }
  return response.data.streamToken;
}

/**
 * Mints a short-lived, single-use stream token for the admin messaging SSE
 * stream. Uses the non-product-scoped `/admin/messaging/stream-token` endpoint
 * (admin auth); the request body is ignored server-side.
 */
export async function apiMintAdminMessagingStreamToken(
  userToken: string | null | undefined
): Promise<string> {
  const response = (await makeNonScopedApiRequest(
    "admin/messaging/stream-token",
    "POST",
    userToken ?? null,
    {}
  )) as { success?: boolean; statusCode?: number; data?: { streamToken?: string } };
  if (!response?.success || !response?.data?.streamToken) {
    throw new Error(
      `Failed to mint admin messaging stream token (status: ${response?.statusCode ?? "unknown"})`
    );
  }
  return response.data.streamToken;
}

// ======================================================================================|
//  MARK: Activity SSE

export function connectProjectActivitySSE({
  projectId,
  getToken,
  onOpen = () => {},
  onInit = () => {},
  onUpdate = () => {},
  onPing = () => {},
  onError = () => {},
  onReconnect = () => {},
}: {
  projectId: string;
  getToken: () => Promise<string | null | undefined>;
  onOpen?: () => void;
  onInit?: (payload: unknown) => void;
  onUpdate?: (payload: unknown) => void;
  onPing?: () => void;
  onError?: (error: any) => void;
  onReconnect?: () => void;
}) {
  if (typeof EventSource === "undefined") {
    throw new Error("EventSource is not available in this environment");
  }
  let es: EventSource | null = null;
  let closed = false;

  const closeEs = () => {
    if (es) {
      try {
        es.close();
      } catch {
        // ignore
      }
      es = null;
    }
  };

  const open = async () => {
    try {
      // Mint a FRESH, single-use stream token for every (re)connect. A replayed
      // or expired token is rejected with 401, so we never reuse one.
      const bearer = await getToken();
      const streamToken = await apiMintStreamToken(bearer, {
        purpose: "project-activity",
        resource: projectId,
      });
      if (closed) return;
      const url = buildApiUrl(`project/activity-sse/${projectId}`, { streamToken });
      const source = openStreamEventSource(url);
      es = source;

      source.onopen = () => {
        try {
          onOpen();
        } catch (err) {
          console.debug("SSE onOpen callback error", err);
        }
      };

      source.addEventListener("init", (e) => {
        try {
          const payload = JSON.parse(e.data);
          onInit(payload);
        } catch {
          onInit(e.data);
        }
      });

      source.addEventListener("update", (e) => {
        try {
          const payload = JSON.parse(e.data);
          onUpdate(payload);
        } catch (err) {
          console.warn("SSE update parse error:", err, e.data);
        }
      });

      source.addEventListener("ping", () => {
        onPing();
      });

      source.addEventListener("reconnect", () => {
        // Server is gracefully closing the connection before hitting CF Worker
        // limits. Close to prevent the browser from auto-retrying with the
        // now-consumed single-use stream token; the caller re-invokes to re-mint.
        closeEs();
        onReconnect();
      });

      source.onerror = (err) => {
        // EventSource onerror passes a native Event object (not an Error).
        // Close to prevent the browser's built-in retry replaying the consumed
        // single-use stream token; the caller re-invokes to re-mint.
        const readyState = source.readyState;
        closeEs();
        if (err instanceof Event) {
          const stateLabel = readyState === EventSource.CONNECTING ? "reconnecting"
            : readyState === EventSource.CLOSED ? "closed"
            : "unknown";
          onError(new Error(`SSE connection error (state: ${stateLabel})`));
        } else {
          onError(err);
        }
      };
    } catch (err) {
      closeEs();
      onError(err instanceof Error ? err : new Error(String(err)));
    }
  };

  void open();

  return () => {
    closed = true;
    closeEs();
  };
}

// ======================================================================================|
//  MARK: Analytics

export async function apiGetAnalyticsOverview(userToken, projectId, params) {
  return await makeApiRequest(
    `${API_SUB_PATHS.project_analytics}/overview/${projectId}`,
    "GET",
    userToken,
    null,
    params,
    { cache: { strategy: "network-only" } }
  );
}

export async function apiGetAnalyticsTimeseries(userToken, projectId, params) {
  return await makeApiRequest(
    `${API_SUB_PATHS.project_analytics}/timeseries/${projectId}`,
    "GET",
    userToken,
    null,
    params,
    { cache: { strategy: "network-only" } }
  );
}

export async function apiGetAnalyticsDetailAsc(userToken, projectId, params) {
  return await makeApiRequest(
    `${API_SUB_PATHS.project_analytics}/detail/asc/${projectId}`,
    "GET",
    userToken,
    null,
    params,
    { cache: { strategy: "network-only" } }
  );
}

// A7Y-5741 — whether an App Store product page optimization test is running.
export async function apiGetProductPageTestState(userToken, projectId) {
  return await makeApiRequest(
    `${API_SUB_PATHS.project_analytics}/product-page-tests/${projectId}`,
    "GET",
    userToken,
    null,
    null,
    { cache: { strategy: "network-only" } }
  );
}

export async function apiGetAnalyticsDetailGpc(userToken, projectId, params) {
  return await makeApiRequest(
    `${API_SUB_PATHS.project_analytics}/detail/gpc/${projectId}`,
    "GET",
    userToken,
    null,
    params,
    { cache: { strategy: "network-only" } }
  );
}

export async function apiGetAnalyticsDrilldown(userToken, projectId, params) {
  return await makeApiRequest(
    `${API_SUB_PATHS.project_analytics}/drilldown/${projectId}`,
    "GET",
    userToken,
    null,
    params,
    { cache: { strategy: "network-only" } }
  );
}

/** A7Y-4670: Google Play visitors, installs and conversion per country per month. */
export async function apiGetAnalyticsMonthlyByCountry(userToken, projectId, params) {
  return await makeApiRequest(
    `${API_SUB_PATHS.project_analytics}/monthly-by-country/${projectId}`,
    "GET",
    userToken,
    null,
    params,
    { cache: { strategy: "network-only" } }
  );
}

/** A7Y-5740: App Store downloads, page views and conversion by territory × source type. */
export async function apiGetAnalyticsSourceTerritory(userToken, projectId, params) {
  return await makeApiRequest(
    `${API_SUB_PATHS.project_analytics}/source-territory/${projectId}`,
    "GET",
    userToken,
    null,
    params,
    { cache: { strategy: "network-only" } }
  );
}

/** A7Y-5742: Android vitals (ANR and crash rate) per app version. */
export async function apiGetAnalyticsVitals(userToken, projectId, params) {
  return await makeApiRequest(
    `${API_SUB_PATHS.project_analytics}/vitals/${projectId}`,
    "GET",
    userToken,
    null,
    params,
    { cache: { strategy: "network-only" } }
  );
}

export async function apiGetAnalyticsFunnel(userToken, projectId, params) {
  return await makeApiRequest(
    `${API_SUB_PATHS.project_analytics}/funnel/${projectId}`,
    "GET",
    userToken,
    null,
    params,
    { cache: { strategy: "network-only" } }
  );
}

export async function apiGetAnalyticsCumulative(userToken, projectId, params) {
  return await makeApiRequest(
    `${API_SUB_PATHS.project_analytics}/cumulative/${projectId}`,
    "GET",
    userToken,
    null,
    params,
    { cache: { strategy: "network-only" } }
  );
}

export async function apiGetAnalyticsBeforeAfter(userToken, projectId, params) {
  return await makeApiRequest(
    `${API_SUB_PATHS.project_analytics}/before-after/${projectId}`,
    "GET",
    userToken,
    null,
    params,
    { cache: { strategy: "network-only" } }
  );
}

export async function apiGetAnalyticsTargets(userToken, projectId) {
  return await makeApiRequest(
    `${API_SUB_PATHS.project_analytics}/targets/${projectId}`,
    "GET",
    userToken,
    null,
    null,
    { cache: { strategy: "network-only" } }
  );
}

export async function apiSetAnalyticsTarget(userToken, projectId, data) {
  return await makeApiRequest(
    `${API_SUB_PATHS.project_analytics}/targets/${projectId}`,
    "PUT",
    userToken,
    data
  );
}

export async function apiDeleteAnalyticsTarget(userToken, projectId, targetId) {
  return await makeApiRequest(
    `${API_SUB_PATHS.project_analytics}/targets/${projectId}/${targetId}`,
    "DELETE",
    userToken
  );
}

// ======================================================================================|
//  MARK: AI Referral Analytics

export async function apiGetAiReferralAnalytics(userToken, projectId, params) {
  return await makeApiRequest(
    `${API_SUB_PATHS.project_analytics}/ai-referrals/${projectId}`,
    "GET",
    userToken,
    null,
    params,
    { cache: { strategy: "network-only" } }
  );
}

// ======================================================================================|
//  MARK: Community Signal

export async function apiGetCommunitySignalReport(userToken, projectId) {
  return await makeApiRequest(
    `${API_SUB_PATHS.project_analytics}/community-signal/${projectId}`,
    "GET",
    userToken,
    null,
    null,
    { cache: { strategy: "network-only" } }
  );
}

export async function apiTriggerCommunitySignalScan(userToken, projectId) {
  return await makeApiRequest(
    `${API_SUB_PATHS.project_analytics}/community-signal/${projectId}/scan`,
    "POST",
    userToken
  );
}

// ======================================================================================|
//  MARK: SoV (Share of Voice)

export async function apiGetSovTrend(userToken, projectId, params) {
  return await makeApiRequest(
    `${API_SUB_PATHS.project_analytics}/sov/${projectId}`,
    "GET",
    userToken,
    null,
    params,
    { cache: { strategy: "network-only" } }
  );
}

export async function apiGetSovLatest(userToken, projectId) {
  return await makeApiRequest(
    `${API_SUB_PATHS.project_analytics}/sov/${projectId}/latest`,
    "GET",
    userToken,
    null,
    null,
    { cache: { strategy: "network-only" } }
  );
}

// ======================================================================================|
//  MARK: Rank History (A7Y-2122)

/**
 * A7Y-3092 — `store` is REQUIRED, and that is the point of the parameter.
 *
 * The defect this replaces was a silent server-side default (`apps.asc ?
 * "appstore" : …`), which served App Store ranks under a Play tab. An optional
 * parameter here would keep that default alive as a live code path; a required
 * one makes a storefront-blind call a compile error rather than a wrong answer.
 */
/**
 * A7Y-4326 — `locale` is REQUIRED, same reasoning as `store` above: rank
 * tracking now captures per (storefront, locale) target, so a locale-blind
 * call is a compile error rather than a silently-wrong default.
 *
 * `terms: ''` is an explicit empty selection and is sent on the wire as
 * `terms=` (an empty query value) — `buildApiUrl` appends any queryParams
 * value that isn't `null`/`undefined`, including `''`. `terms: undefined`
 * omits the param entirely, which the server reads as its own default.
 * `competitors` is joined by `,`; `undefined` omits the param.
 */
export async function apiGetProjectRankHistory(
  userToken: string | null | undefined,
  projectId: string,
  params: {
    startDate: string;
    endDate: string;
    store: RankStoreId;
    locale: string;
    /** A7Y-4703 — one of several markets sharing `locale`; omitted = the locale's derived market. */
    country?: string;
    terms?: string;
    pool?: string;
    competitors?: string[];
  }
): Promise<ApiResult<RankHistoryResponse>> {
  return (await makeApiRequest(
    `${API_SUB_PATHS.project_analytics}/rank-history/${projectId}`,
    "GET",
    userToken,
    null,
    {
      startDate: params.startDate,
      endDate: params.endDate,
      store: params.store,
      locale: params.locale,
      country: params.country,
      terms: params.terms,
      pool: params.pool,
      competitors: params.competitors ? params.competitors.join(",") : undefined,
    },
    { cache: { strategy: "network-only" } }
  )) as ApiResult<RankHistoryResponse>;
}

/**
 * A7Y-4658 — competitor share of voice per keyword, from the BigQuery ledger
 * (`v_project_rank_history`). Same four required params as
 * `apiGetProjectRankHistory` above — a storefront/locale-blind call is a
 * compile error here too.
 */
export async function apiGetProjectRankShareOfVoice(
  userToken: string | null | undefined,
  projectId: string,
  params: { startDate: string; endDate: string; store: RankStoreId; locale: string; country?: string }
): Promise<ApiResult<RankShareOfVoiceResponse>> {
  return (await makeApiRequest(
    `${API_SUB_PATHS.project_analytics}/rank-history/${projectId}/share-of-voice`,
    "GET",
    userToken,
    null,
    {
      startDate: params.startDate,
      endDate: params.endDate,
      store: params.store,
      locale: params.locale,
      country: params.country,
    },
    { cache: { strategy: "network-only" } }
  )) as ApiResult<RankShareOfVoiceResponse>;
}

/**
 * A7Y-4440 — listing rating history for the Rank History rating tile.
 * `store` scopes the target app, as on `apiGetProjectRankShareOfVoice`.
 */
export async function apiGetProjectRatingHistory(
  userToken: string | null | undefined,
  projectId: string,
  params: { store: RankStoreId }
): Promise<ApiResult<RankRatingHistoryResponse>> {
  return (await makeApiRequest(
    `${API_SUB_PATHS.project_analytics}/rank-history/${projectId}/rating-history`,
    "GET",
    userToken,
    null,
    { store: params.store },
    { cache: { strategy: "network-only" } }
  )) as ApiResult<RankRatingHistoryResponse>;
}

/**
 * A7Y-4572 — one app's timeline in one recorded country: chart rank, rating,
 * listing changes, in-app events, featuring and (own app) console lines.
 * `app` is a tracked competitor's store app id; omitted = the project's own app.
 */
export async function apiGetProjectAppTimeline(
  userToken: string | null | undefined,
  projectId: string,
  params: { store: RankStoreId; country: string; app?: string }
): Promise<ApiResult<AppTimelineResponse>> {
  return (await makeApiRequest(
    `${API_SUB_PATHS.project_analytics}/app-timeline/${projectId}`,
    "GET",
    userToken,
    null,
    {
      store: params.store,
      country: params.country,
      ...(params.app ? { app: params.app } : {}),
    },
    { cache: { strategy: "network-only" } }
  )) as ApiResult<AppTimelineResponse>;
}

/**
 * A7Y-4657 — Keyword Explorer market search: `store`/`country`/`language`
 * scope the market (A7Y-5135: language filters the ids), `q` is an optional (possibly empty) prefix filter normalized
 * server-side.
 */
export async function apiSearchKeywordMarket(
  userToken: string | null | undefined,
  projectId: string,
  params: { store: RankStoreId; country: string; language: string; q?: string }
): Promise<ApiResult<KeywordMarketSearchResponse>> {
  return (await makeApiRequest(
    `${API_SUB_PATHS.project_keywords}/search/${projectId}`,
    "GET",
    userToken,
    null,
    {
      store: params.store,
      country: params.country,
      language: params.language,
      q: params.q,
    },
    { cache: { strategy: "network-only" } }
  )) as ApiResult<KeywordMarketSearchResponse>;
}

/**
 * A7Y-4657 — Keyword Explorer term page: volume/difficulty/ranking/
 * neighbours for one `keyword_id` (rejects a `web:` store id server-side).
 */
export async function apiGetKeywordTermPage(
  userToken: string | null | undefined,
  projectId: string,
  params: { keywordId: string }
): Promise<ApiResult<KeywordTermPageResponse>> {
  return (await makeApiRequest(
    `${API_SUB_PATHS.project_keywords}/term/${projectId}`,
    "GET",
    userToken,
    null,
    { keyword_id: params.keywordId },
    { cache: { strategy: "network-only" } }
  )) as ApiResult<KeywordTermPageResponse>;
}

/**
 * A7Y-4715 — Keyword Explorer per-app list: every keyword one app (own or a
 * tracked competitor) was observed ranking for in one store/country/language.
 */
export async function apiGetKeywordAppKeywords(
  userToken: string | null | undefined,
  projectId: string,
  params: { store: RankStoreId; country: string; language: string; appId: string }
): Promise<ApiResult<KeywordAppKeywordsResponse>> {
  return (await makeApiRequest(
    `${API_SUB_PATHS.project_keywords}/app/${projectId}`,
    "GET",
    userToken,
    null,
    {
      store: params.store,
      country: params.country,
      language: params.language,
      app_id: params.appId,
    },
    { cache: { strategy: "network-only" } }
  )) as ApiResult<KeywordAppKeywordsResponse>;
}

/**
 * A7Y-3360 — measure ONE tracked term on ONE storefront, at add time.
 *
 * The add itself never blocks on this: the caller persists the term first and
 * fires this afterwards. BOTH ARMS OF THE RESULT ARE SUCCESSES — a
 * `measured: false` body arrives with HTTP 200 and a named reason, because "we
 * asked and it was too slow", "we asked and it broke" and "there is nothing to
 * ask about" are things to tell the user, not errors to swallow. A transport
 * failure is still an `ApiResult` failure and is distinct from all three.
 */
export async function apiMeasureTrackedKeyword(
  userToken: string | null | undefined,
  input: { projectId: string; term: string; store: RankStoreId; locale: string; country?: string }
): Promise<ApiResult<RankKeywordMeasurement>> {
  return (await makeApiRequest(
    `${API_ROOT_PATHS.project}/${input.projectId}/rank-tracking/measure`,
    "POST",
    userToken,
    {
      term: input.term,
      store: input.store,
      locale: input.locale,
      ...(input.country ? { country: input.country } : {}),
    }
  )) as ApiResult<RankKeywordMeasurement>;
}

// ======================================================================================|
//  MARK: ASO Engine Activity SSE

// Canonical ASO Engine types live in @a7y/types. Re-exported here for
// backward-compatible import paths. Do NOT redeclare — keep a single source of
// truth to prevent the drift that caused A7Y-637.
import type {
  AsoEngineType,
  AsoEngineStatus,
  AsoEngineProgress,
  AsoEngineStartRequest,
  AsoEngineStartResponse,
  AsoEngineOutputResponse,
  AsoEngineChildStatus,
  AsoEngineSseInitEvent,
  AsoEngineSseUpdateEvent,
  AsoEngineSseCompleteEvent,
} from "@a7y/types";
export type {
  AsoEngineType,
  AsoEngineStatus,
  AsoEngineProgress,
  AsoEngineStartRequest,
  AsoEngineStartResponse,
  AsoEngineOutputResponse,
  AsoEngineChildStatus,
  AsoEngineSseInitEvent,
  AsoEngineSseUpdateEvent,
  AsoEngineSseCompleteEvent,
};

/**
 * Start an ASO engine run.
 */
export async function apiStartAsoEngine(userToken, request: AsoEngineStartRequest) {
  if (doLog()) console.log("apiStartAsoEngine() request", request);
  return await makeApiRequest(
    "aso/engine/start",
    "POST",
    userToken,
    request,
    null,
    { cache: { strategy: "network-only" } }
  );
}

/**
 * Get the output of an ASO engine run.
 */
export async function apiGetAsoEngineOutput(userToken, instanceId: string) {
  if (doLog()) console.log("apiGetAsoEngineOutput() instanceId", instanceId);
  return await makeApiRequest(
    `aso/engine/output/${instanceId}`,
    "GET",
    userToken,
    null,
    null,
    { cache: { strategy: "network-only" } }
  );
}

/**
 * Get active and recent audit engine activity for an app.
 */
export async function apiGetAuditActivity(
  userToken,
  params: {
    appIdentifier: string;
    countryCode: string;
    languageCode?: string;
    locale?: string;
  }
) {
  const queryParams: Record<string, string> = {
    appIdentifier: params.appIdentifier,
    countryCode: params.countryCode,
  };
  if (params.languageCode) {
    queryParams.languageCode = params.languageCode;
  }
  if (params.locale) {
    queryParams.locale = params.locale;
  }
  return await makeApiRequest(
    "aso/engine/audit-activity",
    "GET",
    userToken,
    null,
    queryParams,
    { cache: { strategy: "network-only" } }
  );
}

/**
 * Connect to an ASO engine activity SSE stream.
 *
 * @param options - Connection options
 * @returns Disconnect function
 */
export function connectEngineActivitySSE({
  instanceId,
  getToken,
  onOpen = () => {},
  onInit = () => {},
  onUpdate = () => {},
  onComplete = () => {},
  onPing = () => {},
  onError = () => {},
  onReconnect = () => {},
}: {
  instanceId: string;
  getToken: () => Promise<string | null | undefined>;
  onOpen?: () => void;
  onInit?: (payload: AsoEngineSseInitEvent) => void;
  onUpdate?: (payload: AsoEngineSseUpdateEvent) => void;
  onComplete?: (payload: AsoEngineSseCompleteEvent) => void;
  onPing?: () => void;
  onError?: (error: unknown) => void;
  onReconnect?: () => void;
}) {
  if (typeof EventSource === "undefined") {
    throw new Error("EventSource is not available in this environment");
  }
  let es: EventSource | null = null;
  let closed = false;

  const closeEs = () => {
    if (es) {
      try {
        es.close();
      } catch {
        // ignore
      }
      es = null;
    }
  };

  const open = async () => {
    try {
      // Mint a FRESH, single-use stream token for every (re)connect.
      const bearer = await getToken();
      const streamToken = await apiMintStreamToken(bearer, {
        purpose: "aso-engine",
        resource: instanceId,
      });
      if (closed) return;
      const url = buildApiUrl(`aso/engine/stream/${instanceId}`, { streamToken });
      const source = openStreamEventSource(url);
      es = source;

      source.onopen = () => {
        try {
          onOpen();
        } catch (err) {
          console.debug("Engine SSE onOpen callback error", err);
        }
      };

      source.addEventListener("init", (e) => {
        try {
          const payload = JSON.parse(e.data);
          onInit(payload);
        } catch {
          onInit(e.data as unknown as AsoEngineSseInitEvent);
        }
      });

      source.addEventListener("update", (e) => {
        try {
          const payload = JSON.parse(e.data);
          onUpdate(payload);
        } catch (err) {
          console.warn("Engine SSE update parse error:", err, e.data);
        }
      });

      source.addEventListener("complete", (e) => {
        try {
          const payload = JSON.parse(e.data);
          onComplete(payload);
        } catch (err) {
          console.warn("Engine SSE complete parse error:", err, e.data);
        }
      });

      source.addEventListener("ping", () => {
        onPing();
      });

      source.addEventListener("reconnect", () => {
        // Server is gracefully closing the connection before hitting CF Worker
        // limits. Close to prevent the browser from auto-retrying with the
        // now-consumed single-use stream token; the caller re-invokes to re-mint.
        closeEs();
        onReconnect();
      });

      source.onerror = (err) => {
        // EventSource onerror passes a native Event object (not an Error).
        // Close to prevent the browser's built-in retry replaying the consumed
        // single-use stream token; the caller re-invokes to re-mint.
        const readyState = source.readyState;
        closeEs();
        if (err instanceof Event) {
          const stateLabel = readyState === EventSource.CONNECTING ? "reconnecting"
            : readyState === EventSource.CLOSED ? "closed"
            : "unknown";
          onError(new Error(`SSE connection error (state: ${stateLabel})`));
        } else {
          onError(err);
        }
      };
    } catch (err) {
      closeEs();
      onError(err instanceof Error ? err : new Error(String(err)));
    }
  };

  void open();

  return () => {
    closed = true;
    closeEs();
  };
}

// ======================================================================================|
//  MARK: Revisions

export async function apiGetDraftRevision(
  userToken,
  projectId: string,
  storeId: string,
  locale?: string,
) {
  if (doLog())
    console.log(
      "apiGetDraftRevision() projectId storeId locale",
      projectId,
      storeId,
      locale,
    );
  return await makeApiRequest(
    `${API_ROOT_PATHS.project}/revisions/draft/${projectId}/${storeId}`,
    "GET",
    userToken,
    null,
    locale ? { locale } : null,
    { cache: { strategy: "network-only" } }
  );
}

export async function apiUpdateDraftRevisionField(
  userToken,
  projectId: string,
  storeId: string,
  field: string,
  value: string,
  locale?: string,
) {
  if (doLog())
    console.log(
      "apiUpdateDraftRevisionField() projectId storeId field locale",
      projectId,
      storeId,
      field,
      locale,
    );
  return await makeApiRequest(
    `${API_ROOT_PATHS.project}/revisions/draft/${projectId}/${storeId}`,
    "PATCH",
    userToken,
    locale ? { field, value, locale } : { field, value }
  );
}

/**
 * A7Y-510: trigger a full re-translation of the given locale's `localeData`
 * slot on the project's current draft revision. Fire-and-forget on the
 * server side; poll `apiGetDraftRevision(..., locale)` to watch the slot
 * populate.
 */
export async function apiRetranslateDraftRevisionLocale(
  userToken,
  projectId: string,
  storeId: string,
  locale: string,
) {
  if (doLog())
    console.log(
      "apiRetranslateDraftRevisionLocale() projectId storeId locale",
      projectId,
      storeId,
      locale,
    );
  return await makeApiRequest(
    `${API_ROOT_PATHS.project}/revisions/draft/${projectId}/${storeId}/retranslate/${locale}`,
    "POST",
    userToken,
  );
}

export async function apiGetRevisionHistory(userToken, projectId: string, storeId: string) {
  if (doLog()) console.log("apiGetRevisionHistory() projectId storeId", projectId, storeId);
  return await makeApiRequest(
    `${API_ROOT_PATHS.project}/revisions/history/${projectId}/${storeId}`,
    "GET",
    userToken,
    null,
    null,
    { cache: { strategy: "network-only" } }
  );
}

/** A7Y-4684: tag the working draft with the app version it is meant for (`null` clears it). */
export async function apiUpdateDraftRevisionAppVersion(
  userToken,
  projectId: string,
  storeId: string,
  appVersion: string | null,
) {
  if (doLog()) console.log("apiUpdateDraftRevisionAppVersion() projectId storeId", projectId, storeId);
  return await makeApiRequest(
    `${API_ROOT_PATHS.project}/revisions/draft/${projectId}/${storeId}/app-version`,
    "PUT",
    userToken,
    { appVersion },
  );
}

/** A7Y-4684: copy a published set's text back into the working draft, field by field. */
export async function apiCopyRevisionIntoDraft(
  userToken,
  projectId: string,
  storeId: string,
  revisionId: string,
) {
  if (doLog()) console.log("apiCopyRevisionIntoDraft() projectId storeId revisionId", projectId, storeId, revisionId);
  return await makeApiRequest(
    `${API_ROOT_PATHS.project}/revisions/draft/${projectId}/${storeId}/copy-from/${encodeURIComponent(revisionId)}`,
    "POST",
    userToken,
    {},
  );
}

export async function apiGetRevision(userToken, revisionId: string) {
  return await makeApiRequest(
    `${API_ROOT_PATHS.project}/revisions/${revisionId}`,
    "GET",
    userToken
  );
}

export async function apiUpdateDraftRevisionIcon(userToken, projectId: string, storeId: string, url: string, originUrl?: string) {
  return await makeApiRequest(
    `${API_ROOT_PATHS.project}/revisions/draft/${projectId}/${storeId}/icon`,
    "PATCH",
    userToken,
    { url, originUrl }
  );
}

export async function apiUpdateDraftRevisionScreenshots(userToken, projectId: string, storeId: string, screenshots) {
  return await makeApiRequest(
    `${API_ROOT_PATHS.project}/revisions/draft/${projectId}/${storeId}/screenshots`,
    "PATCH",
    userToken,
    { screenshots }
  );
}

// ======================================================================================|
//  MARK: Content Engine Adoption

export async function apiGetSuggestedContent(userToken, projectId: string, storeId: string, locale?: string) {
  return await makeApiRequest(
    `${API_ROOT_PATHS.project}/revisions/suggested-content/${projectId}/${storeId}${locale ? `?locale=${encodeURIComponent(locale)}` : ""}`,
    "GET",
    userToken,
    null,
    null,
    { cache: { strategy: "network-only" } }
  );
}

export async function apiGetHypothesisAlternative(userToken, projectId: string, storeId: string, hypothesisId: string) {
  return await makeApiRequest(
    `${API_ROOT_PATHS.project}/revisions/suggested-content/${projectId}/${storeId}/alternatives/${hypothesisId}`,
    "GET",
    userToken
  );
}

export async function apiGetFieldAlternatives(userToken, projectId: string, storeId: string, fieldKey: string) {
  // The API returns 200 with `alternatives: []` when no suggested-content
  // revision exists for this project / storefront / field yet — so no silent
  // 404 handling is needed here.
  return await makeApiRequest(
    `${API_ROOT_PATHS.project}/revisions/suggested-content/${projectId}/${storeId}/field/${fieldKey}`,
    "GET",
    userToken,
  );
}

export async function apiAdoptHypothesis(userToken, projectId: string, storeId: string, body) {
  return await makeApiRequest(
    `${API_ROOT_PATHS.project}/revisions/draft/${projectId}/${storeId}/adopt-hypothesis`,
    "POST",
    userToken,
    body
  );
}

export async function apiAdoptField(userToken, projectId: string, storeId: string, body) {
  return await makeApiRequest(
    `${API_ROOT_PATHS.project}/revisions/draft/${projectId}/${storeId}/adopt-field`,
    "POST",
    userToken,
    body
  );
}

export async function apiAdoptFields(userToken, projectId: string, storeId: string, body) {
  return await makeApiRequest(
    `${API_ROOT_PATHS.project}/revisions/draft/${projectId}/${storeId}/adopt-fields`,
    "POST",
    userToken,
    body
  );
}

// A7Y-4685: the store policy checker over one locale's Google Play fields. Read-only.
export async function apiCheckListingPolicy(
  userToken,
  projectId: string,
  storeId: string,
  body: ListingPolicyCheckRequest
) {
  return await makeApiRequest(
    `${API_ROOT_PATHS.project}/revisions/policy-check/${projectId}/${storeId}`,
    "POST",
    userToken,
    body
  );
}

// ======================================================================================|
//  MARK: Revision Publishing

export async function apiPublishRevision(
  userToken,
  projectId: string,
  request: { revisionId: string; storeId: string; exclusions?: RevisionPublishExclusions }
) {
  if (doLog()) console.log("apiPublishRevision() projectId", projectId);
  return await makeApiRequest(
    `${API_ROOT_PATHS.project}/revisions/${projectId}/publish`,
    "POST",
    userToken,
    request,
    null,
    { cache: { strategy: "network-only" } }
  );
}

export function connectPublishSSE({
  projectId,
  publishId,
  getToken,
  onOpen = () => {},
  onInit = () => {},
  onStatus = () => {},
  onProgress = () => {},
  onComplete = () => {},
  onError = () => {},
  onReconnect = () => {},
}: {
  projectId: string;
  publishId: string;
  getToken: () => Promise<string | null | undefined>;
  onOpen?: () => void;
  onInit?: (payload: unknown) => void;
  onStatus?: (payload: any) => void;
  onProgress?: (payload: any) => void;
  onComplete?: (payload: any) => void;
  onError?: (error: any) => void;
  onReconnect?: () => void;
}) {
  if (typeof EventSource === "undefined") {
    throw new Error("EventSource is not available in this environment");
  }
  let es: EventSource | null = null;
  let closed = false;

  const closeEs = () => {
    if (es) {
      try {
        es.close();
      } catch {
        // ignore
      }
      es = null;
    }
  };

  const open = async () => {
    try {
      // Mint a FRESH, single-use stream token for every (re)connect.
      const bearer = await getToken();
      const streamToken = await apiMintStreamToken(bearer, {
        purpose: "publish-progress",
        resource: publishId,
      });
      if (closed) return;
      const url = buildApiUrl(
        `project/revisions/${projectId}/publish-sse/${publishId}`,
        { streamToken }
      );
      const source = openStreamEventSource(url);
      es = source;

      source.onopen = () => {
        try {
          onOpen();
        } catch (err) {
          console.debug("SSE onOpen callback error", err);
        }
      };

      source.addEventListener("init", (e) => {
        try {
          const payload = JSON.parse((e as MessageEvent).data);
          onInit(payload);
        } catch {
          onInit((e as MessageEvent).data);
        }
      });

      source.addEventListener("status", (e) => {
        try {
          const payload = JSON.parse((e as MessageEvent).data);
          onStatus(payload);
        } catch (err) {
          console.warn("SSE status parse error:", err);
        }
      });

      source.addEventListener("progress", (e) => {
        try {
          const payload = JSON.parse((e as MessageEvent).data);
          onProgress(payload);
        } catch (err) {
          console.warn("SSE progress parse error:", err);
        }
      });

      source.addEventListener("complete", (e) => {
        try {
          const payload = JSON.parse((e as MessageEvent).data);
          onComplete(payload);
        } catch (err) {
          console.warn("SSE complete parse error:", err);
        }
      });

      source.addEventListener("ping", () => {
        // keepalive, no-op
      });

      source.addEventListener("reconnect", () => {
        // Close to prevent the browser from auto-retrying with the consumed
        // single-use stream token; the caller re-invokes to re-mint.
        closeEs();
        onReconnect();
      });

      source.onerror = (err) => {
        // Close to prevent the browser's built-in retry replaying the consumed
        // single-use stream token.
        closeEs();
        try {
          onError(err);
        } catch (callbackErr) {
          console.debug("SSE onError callback error", callbackErr);
        }
      };
    } catch (err) {
      closeEs();
      try {
        onError(err);
      } catch (callbackErr) {
        console.debug("SSE onError callback error", callbackErr);
      }
    }
  };

  void open();

  return () => {
    closed = true;
    closeEs();
  };
}

// ======================================================================================|
//  MARK: Revision Translations

export async function apiTranslateSingleField(userToken, body) {
  return await makeApiRequest(
    `${API_ROOT_PATHS.project}/translations/translate-field`,
    "POST",
    userToken,
    body
  );
}

export async function apiEditRevisionTranslation(
  userToken,
  revisionId: string,
  fieldKey: string,
  locale: string,
  projectId: string,
  translation: string,
) {
  return await makeApiRequest(
    `${API_SUB_PATHS.project_translations}/revision/${revisionId}/${fieldKey}/${locale}`,
    "PATCH",
    userToken,
    { projectId, translation }
  );
}

export async function apiGetRevisionFieldManualEdits(
  userToken,
  revisionId: string,
  fieldKey: string,
  projectId: string,
) {
  return await makeApiRequest(
    `${API_SUB_PATHS.project_translations}/revision/${revisionId}/${fieldKey}/manual-edits`,
    "GET",
    userToken,
    null,
    { projectId }
  );
}

export async function apiGetRevisionTranslationStatus(
  userToken,
  revisionId: string,
  projectId: string,
) {
  return await makeApiRequest(
    `${API_SUB_PATHS.project_translations}/revision/${revisionId}/status`,
    "GET",
    userToken,
    null,
    { projectId }
  );
}

export async function apiGetRevisionFieldTranslations(
  userToken,
  revisionId: string,
  fieldKey: string,
  projectId: string,
  locale?: string,
) {
  return await makeApiRequest(
    `${API_SUB_PATHS.project_translations}/revision/${revisionId}/${fieldKey}`,
    "GET",
    userToken,
    null,
    { projectId, ...(locale ? { locale } : {}) }
  );
}

// ======================================================================================|
// MARK: Store Search

export async function apiStoreSearch(
  userToken,
  params: {
    term: string;
    storeId: "appstore" | "playstore";
    country?: string;
    lang?: string;
    limit?: number;
  }
) {
  return await makeApiRequest(
    "aso/store-search/search",
    "GET",
    userToken,
    null,
    {
      term: params.term,
      storeId: params.storeId,
      country: params.country ?? "us",
      ...(params.lang ? { lang: params.lang } : {}),
      ...(params.limit !== undefined ? { limit: String(params.limit) } : {}),
    },
    { cache: { strategy: "network-only" } }
  );
}

/**
 * App Search Autocomplete.
 *
 * Hits `GET /aso/store-search/autocomplete?term=...` which fans out to
 * Algolia + (if needed) iTunes + SearchAPI in parallel. Used by the
 * project-creation flow to suggest apps as the user types.
 *
 * Network-only on purpose: the upstream layer already deduplicates and the
 * suggestions are time-sensitive (we don't want stale results from an
 * earlier session).
 */
export async function apiStoreSearchAutocomplete(
  userToken: string | null,
  term: string,
): Promise<{
  success: boolean;
  results?: import("@a7y/types").AppAutocompleteItem[];
  complete?: boolean;
  error?: string;
}> {
  // `makeApiRequest` wraps the body as `{ success, statusCode, data }`; every
  // caller reads `results` at the top level, so unwrap here. Returning the
  // envelope left `results` undefined, and live search returned nothing.
  const response = (await makeApiRequest(
    "aso/store-search/autocomplete",
    "GET",
    userToken,
    null,
    { term },
    { cache: { strategy: "network-only" } }
  )) as {
    success?: boolean;
    data?: {
      success?: boolean;
      results?: import("@a7y/types").AppAutocompleteItem[];
      complete?: boolean;
      error?: string;
    };
    error?: { message?: string; error?: string };
  };
  if (!response?.success || !response.data) {
    return {
      success: false,
      error: response?.error?.error ?? response?.error?.message ?? "Autocomplete failed",
    };
  }
  return { ...response.data, success: response.data.success !== false };
}

// ======================================================================================|
// MARK: Messaging

export async function apiListThreads(
  userToken: string,
  params?: { limit?: number; startAfter?: string }
) {
  const qp: Record<string, string> = {};
  if (params?.limit) qp.limit = String(params.limit);
  if (params?.startAfter) qp.startAfter = params.startAfter;
  return await makeNonScopedApiRequest(
    `${API_ROOT_PATHS.messaging}/threads`,
    "GET",
    userToken,
    null,
    Object.keys(qp).length > 0 ? qp : null
  );
}

export async function apiCreateThread(userToken: string, request: unknown) {
  return await makeNonScopedApiRequest(
    `${API_ROOT_PATHS.messaging}/threads`,
    "POST",
    userToken,
    request
  );
}

/**
 * A7Y-5479: transcribe a feedback voice note. The recording goes up as the raw
 * request body under its own MIME type; the response is `{ text }`. The audio
 * is not stored server-side.
 */
export async function apiTranscribeFeedbackAudio(userToken: string, audio: Blob) {
  return await makeNonScopedApiRequest(
    `${API_ROOT_PATHS.messaging}/feedback/transcriptions`,
    "POST",
    userToken,
    audio
  );
}

export async function apiGetThread(userToken: string, threadId: string) {
  return await makeNonScopedApiRequest(
    `${API_ROOT_PATHS.messaging}/threads/${threadId}`,
    "GET",
    userToken
  );
}

export async function apiGetThreadMessages(
  userToken: string,
  threadId: string,
  params?: { limit?: number; startAfter?: string }
) {
  const qp: Record<string, string> = {};
  if (params?.limit) qp.limit = String(params.limit);
  if (params?.startAfter) qp.startAfter = params.startAfter;
  return await makeNonScopedApiRequest(
    `${API_ROOT_PATHS.messaging}/threads/${threadId}/messages`,
    "GET",
    userToken,
    null,
    Object.keys(qp).length > 0 ? qp : null
  );
}

export async function apiSendMessage(
  userToken: string,
  threadId: string,
  request: unknown
) {
  return await makeNonScopedApiRequest(
    `${API_ROOT_PATHS.messaging}/threads/${threadId}/messages`,
    "POST",
    userToken,
    request
  );
}

export async function apiGetUploadUrl(userToken: string, request: unknown) {
  return await makeNonScopedApiRequest(
    `${API_ROOT_PATHS.messaging}/upload-url`,
    "POST",
    userToken,
    request
  );
}

export async function apiGetUnreadCount(userToken: string) {
  return await makeNonScopedApiRequest(
    `${API_ROOT_PATHS.messaging}/unread`,
    "GET",
    userToken
  );
}

export async function apiMarkThreadRead(userToken: string, threadId: string) {
  return await makeNonScopedApiRequest(
    `${API_ROOT_PATHS.messaging}/threads/${threadId}/read`,
    "POST",
    userToken
  );
}

// ======================================================================================|
// MARK: Project Notifications (A7Y-1060)
//
// Per-project notification feed: feed list, unread count, and read-state
// toggles. All routes are project-scoped and require a viewer role. The
// API returns the `ProjectNotification*` shapes from `@a7y/types`; the
// `makeApiRequest` wrapper here adds the standard `{ success, statusCode,
// data }` envelope on top, so consumers read the typed body via
// `result.data` (matching the messaging-context pattern).

export async function apiListProjectNotifications(
  userToken: string | null | undefined,
  projectId: string,
  params?: { limit?: number; startAfter?: string }
): Promise<ApiResult<ProjectNotificationListResponse>> {
  if (doLog()) console.log("apiListProjectNotifications()", projectId, params);
  const queryParams: Record<string, string | null | undefined> = {};
  if (params?.limit != null) queryParams.limit = String(params.limit);
  if (params?.startAfter) queryParams.startAfter = params.startAfter;
  return (await makeApiRequest(
    `${API_ROOT_PATHS.project}/${projectId}/notifications`,
    "GET",
    userToken,
    null,
    Object.keys(queryParams).length > 0 ? queryParams : null,
    { cache: { strategy: "network-only" } }
  )) as ApiResult<ProjectNotificationListResponse>;
}

export async function apiGetProjectNotificationsUnreadCount(
  userToken: string | null | undefined,
  projectId: string
): Promise<ApiResult<ProjectNotificationUnreadCountResponse>> {
  if (doLog()) console.log("apiGetProjectNotificationsUnreadCount()", projectId);
  return (await makeApiRequest(
    `${API_ROOT_PATHS.project}/${projectId}/notifications/unread-count`,
    "GET",
    userToken,
    null,
    null,
    { cache: { strategy: "network-only" } }
  )) as ApiResult<ProjectNotificationUnreadCountResponse>;
}

export async function apiMarkProjectNotificationRead(
  userToken: string | null | undefined,
  projectId: string,
  notificationId: string
) {
  if (doLog())
    console.log("apiMarkProjectNotificationRead()", projectId, notificationId);
  return await makeApiRequest(
    `${API_ROOT_PATHS.project}/${projectId}/notifications/${notificationId}/read`,
    "POST",
    userToken
  );
}

export async function apiMarkProjectNotificationUnread(
  userToken: string | null | undefined,
  projectId: string,
  notificationId: string
) {
  if (doLog())
    console.log("apiMarkProjectNotificationUnread()", projectId, notificationId);
  return await makeApiRequest(
    `${API_ROOT_PATHS.project}/${projectId}/notifications/${notificationId}/unread`,
    "POST",
    userToken
  );
}

export async function apiMarkAllProjectNotificationsRead(
  userToken: string | null | undefined,
  projectId: string,
  params?: { startAfter?: string }
) {
  if (doLog()) console.log("apiMarkAllProjectNotificationsRead()", projectId, params);
  // A7Y-4431: forward the `startAfter` cursor the route returns as
  // `lastEventAt` on a hard-cap page, so a caller re-invoking this after
  // `more: true` resumes from the next page instead of re-marking page 1.
  const queryParams: Record<string, string | null | undefined> = {};
  if (params?.startAfter) queryParams.startAfter = params.startAfter;
  return await makeApiRequest(
    `${API_ROOT_PATHS.project}/${projectId}/notifications/mark-all-read`,
    "POST",
    userToken,
    null,
    Object.keys(queryParams).length > 0 ? queryParams : null
  );
}

/**
 * Opens an SSE connection to the messaging unread-updates endpoint.
 * Follows the same pattern as connectProjectActivitySSE.
 *
 * Returns a disconnect function that closes the EventSource.
 */
export function connectMessagingSSE({
  getToken,
  onOpen = () => {},
  onInit = () => {},
  onUpdate = () => {},
  onPing = () => {},
  onError = () => {},
  onReconnect = () => {},
}: {
  getToken: () => Promise<string | null | undefined>;
  onOpen?: () => void;
  onInit?: (payload: unknown) => void;
  onUpdate?: (payload: unknown) => void;
  onPing?: () => void;
  onError?: (error: any) => void;
  onReconnect?: () => void;
}) {
  if (typeof EventSource === "undefined") {
    throw new Error("EventSource is not available in this environment");
  }
  let es: EventSource | null = null;
  let closed = false;

  const closeEs = () => {
    if (es) {
      try {
        es.close();
      } catch {
        // ignore
      }
      es = null;
    }
  };

  const open = async () => {
    try {
      // Mint a FRESH, single-use stream token for every (re)connect. The
      // resource is forced server-side to the caller's userId, so we pass "".
      const bearer = await getToken();
      const streamToken = await apiMintStreamToken(bearer, {
        purpose: "messaging",
        resource: "",
      });
      if (closed) return;
      const url = buildNonScopedApiUrl(`${API_ROOT_PATHS.messaging}/sse`, {
        streamToken,
      });
      const source = openStreamEventSource(url);
      es = source;

      source.onopen = () => {
        try {
          onOpen();
        } catch (err) {
          console.debug("Messaging SSE onOpen callback error", err);
        }
      };

      source.addEventListener("init", (e) => {
        try {
          const payload = JSON.parse(e.data);
          onInit(payload);
        } catch {
          onInit(e.data);
        }
      });

      source.addEventListener("update", (e) => {
        try {
          const payload = JSON.parse(e.data);
          onUpdate(payload);
        } catch (err) {
          console.warn("Messaging SSE update parse error:", err, e.data);
        }
      });

      source.addEventListener("ping", () => {
        onPing();
      });

      source.addEventListener("reconnect", () => {
        // Server is gracefully closing the connection before hitting CF Worker
        // limits. Close to prevent the browser from auto-retrying with the
        // now-consumed single-use stream token; the caller re-invokes to re-mint.
        closeEs();
        onReconnect();
      });

      source.onerror = (err) => {
        // Close to prevent the browser's built-in retry replaying the consumed
        // single-use stream token; the caller re-invokes to re-mint.
        const readyState = source.readyState;
        closeEs();
        if (err instanceof Event) {
          const stateLabel =
            readyState === EventSource.CONNECTING
              ? "reconnecting"
              : readyState === EventSource.CLOSED
                ? "closed"
                : "unknown";
          onError(new Error(`Messaging SSE connection error (state: ${stateLabel})`));
        } else {
          onError(err);
        }
      };
    } catch (err) {
      closeEs();
      onError(err instanceof Error ? err : new Error(String(err)));
    }
  };

  void open();

  return () => {
    closed = true;
    closeEs();
  };
}

/**
 * Opens an SSE connection to the admin messaging unread-updates endpoint.
 * Same pattern as connectMessagingSSE but hits `/admin/messaging/sse`.
 */
export function connectAdminMessagingSSE({
  getToken,
  onOpen = () => {},
  onInit = () => {},
  onUpdate = () => {},
  onPing = () => {},
  onError = () => {},
  onReconnect = () => {},
}: {
  /**
   * Clerk's `getToken`. The options argument is part of the contract here
   * because `open()` below asks for an uncached JWT — see the comment there.
   */
  getToken: (opts?: { skipCache?: boolean }) => Promise<string | null | undefined>;
  onOpen?: () => void;
  onInit?: (payload: unknown) => void;
  onUpdate?: (payload: unknown) => void;
  onPing?: () => void;
  onError?: (error: any) => void;
  onReconnect?: () => void;
}) {
  if (typeof EventSource === "undefined") {
    throw new Error("EventSource is not available in this environment");
  }
  let es: EventSource | null = null;
  let closed = false;

  const closeEs = () => {
    if (es) {
      try {
        es.close();
      } catch {
        // ignore
      }
      es = null;
    }
  };

  const open = async () => {
    try {
      // Mint a FRESH, single-use stream token for every (re)connect.
      //
      // `skipCache` forces a new Clerk session JWT rather than the cached one.
      // A reconnect is usually triggered by the tab becoming visible again,
      // and a tab that was hidden has been holding a JWT that Clerk's refresh
      // loop stopped renewing — so the cached token is precisely the one most
      // likely to be expired at this moment. Measured in the local preview on
      // 2026-09-15: two `POST /admin/messaging/stream-token 401`s on wake,
      // `JWT is expired. Expiry date: 11:25:03, Current date: 11:39:14`,
      // succeeding on the third attempt once Clerk had caught up.
      //
      // This is a per-connection call, not a per-request one, so it costs one
      // Clerk hit per (re)connect and needs no retry — the same trade
      // `useActivityMeter` makes for its heartbeat.
      const bearer = await getToken({ skipCache: true });
      const streamToken = await apiMintAdminMessagingStreamToken(bearer);
      if (closed) return;
      const url = buildNonScopedApiUrl("admin/messaging/sse", { streamToken });
      const source = openStreamEventSource(url);
      es = source;

      source.onopen = () => {
        try {
          onOpen();
        } catch (err) {
          console.debug("Admin messaging SSE onOpen callback error", err);
        }
      };

      source.addEventListener("init", (e) => {
        try {
          const payload = JSON.parse(e.data);
          onInit(payload);
        } catch {
          onInit(e.data);
        }
      });

      source.addEventListener("update", (e) => {
        try {
          const payload = JSON.parse(e.data);
          onUpdate(payload);
        } catch (err) {
          console.warn("Admin messaging SSE update parse error:", err, e.data);
        }
      });

      source.addEventListener("ping", () => {
        onPing();
      });

      source.addEventListener("reconnect", () => {
        // Close to prevent the browser from auto-retrying with the consumed
        // single-use stream token; the caller re-invokes to re-mint.
        closeEs();
        onReconnect();
      });

      source.onerror = (err) => {
        // Close to prevent the browser's built-in retry replaying the consumed
        // single-use stream token; the caller re-invokes to re-mint.
        const readyState = source.readyState;
        closeEs();
        if (err instanceof Event) {
          const stateLabel =
            readyState === EventSource.CONNECTING
              ? "reconnecting"
              : readyState === EventSource.CLOSED
                ? "closed"
                : "unknown";
          onError(
            new Error(`Admin messaging SSE connection error (state: ${stateLabel})`)
          );
        } else {
          onError(err);
        }
      };
    } catch (err) {
      closeEs();
      onError(err instanceof Error ? err : new Error(String(err)));
    }
  };

  void open();

  return () => {
    closed = true;
    closeEs();
  };
}

// Marketing API endpoints (contact, signup, teaser report, turnstile)
export * from "./marketing.js";

// Shared URL constants
export * from "./consts.js";

export * from "./docs-registry.js";

export async function apiSavePreLaunchDraft(userToken: string, projectId: string, storeId: string, draft: import('@a7y/types').PreLaunchDraftRequest) {
  return makeApiRequest(`${API_ROOT_PATHS.project}/revisions/draft/${encodeURIComponent(projectId)}/${storeId}/pre-launch`, 'PUT', userToken, draft);
}

export async function apiUploadDraftCreative(userToken: string, projectId: string, storeId: string, file: File): Promise<{ key: string; url: string; width: number; height: number }> {
  if (!userToken) throw new Error('Sign in to upload an image.');
  const url = buildApiUrl(`${API_ROOT_PATHS.project}/revisions/draft/${encodeURIComponent(projectId)}/${storeId}/creative`, null);
  const response = await (FETCH_FN ?? fetch)(url, { method: 'POST', headers: { Authorization: `Bearer ${userToken}`, 'Content-Type': file.type }, body: file });
  const result = await response.json() as { key: string; url: string; width: number; height: number; message?: string };
  if (!response.ok) throw new Error(result.message ?? 'Image upload failed.');
  return result;
}
